import { useEffect, useRef, useState } from 'react';
import {
  LUMBER_SIZES, PLY_THICKNESSES, lumberDims, formatInches, formatFeet,
  parseFeetInches, sheetOutlines, angledLumberTriangles, endOffsets, isSquare,
  clipPolyHalfPlane, pointInPoly, polyBounds, polyIsRect, polySignedArea,
  parseSignedFeetInches, formatSignedFeet, lumberSolid, sheetSolid,
  solidsCollide, solidIntersectionPoints, mergeIntervals, complementSpans,
  pairStationsToIntervals, maskComponents, closeMask, componentAt, maskOutlines,
} from './mmdlSketchUtils';
import type { LumberSizeKey, RectCut, EndCut, Pt, ConvexSolid } from './mmdlSketchUtils';

// SketchEngine3D (CR #208) — the complete 3D sketch engine extracted from
// MmdlViewer (CR #207 waves 1–7) so any host can feed it a model and get the
// full toolkit: lumber/plywood placement, mitre/bevel/compound cuts, sheet
// line-cuts and cut-outs, Autocut vs pieces AND vs the passive model, Lay /
// Line / Layout / Lay-run, the Generate menu (roof-plane sheathing, purlins,
// sub fascia, gable ladder), glide/magnet drag, measure, the precision panel,
// the keyboard layer, and global sketch undo/redo.
//
// Hosts supply:
//  - loadModel(): the passive model as EngineObject[] — triangle meshes in
//    Z-UP FEET (the .mmdl native frame; the engine converts to three's Y-up).
//    Called on every activation, so a host can re-derive the model each time.
//  - modelKey: rebuild the scene when this changes (mmdl: the file url;
//    drafting: a fingerprint of the plan geometry).
//  - layers: the layer-toggle chips; a mesh's EngineObject.type picks its
//    layer. One layer may be catchAll: toggling it also hides types without
//    their own chip.
//  - adapter/adapterKey: sketch persistence {load, save}. The sketch is
//    loaded once per adapterKey and autosaved (2 s debounce) through save().
//
// ALL model/sketch math is in FEET; UI lengths are carpenter feet-inches.
// MmdlViewer wraps this with the mmdl geometry endpoint + Plan/Help tabs;
// the drafting suite wraps it with plan-extrusion (see lib/drafting/planTo3d).

export interface EngineMesh { color: [number, number, number, number]; positions: number[]; triangles: number[] }
export interface EngineObject { type: string; name: string; meshes: EngineMesh[] }
export interface EngineLayer { key: string; label: string; swatch: string; catchAll?: boolean }
export interface SketchAdapter {
  load: () => Promise<unknown>;               // resolves the stored sketch JSON (or null)
  save: (data: SketchData) => Promise<void>;  // persists the sketch JSON
}
type MmdlObject = EngineObject;

type V3 = [number, number, number];
// Lumber: lengthIn is LONG-POINT to long-point (carpenter convention).
// e0/e1 = angled left/right ends (mitre/bevel degrees); absent = square.
interface LumberItem { id: string; kind?: 'lumber'; size: LumberSizeKey; lengthIn: number; pos: V3; rot: V3; e0?: EndCut; e1?: EndCut }
// Sheets: poly (inches, sheet-frame outline) appears once a sheet has been
// line-cut at an angle; absent = the plain [0,L]×[0,W] rectangle.
interface SheetItem { id: string; kind: 'sheet'; thickIn: number; widthIn: number; lengthIn: number; cuts: RectCut[]; poly?: Pt[]; pos: V3; rot: V3 }
type SketchItem = LumberItem | SheetItem;
interface SketchMeasurement { id: string; a: V3; b: V3 }
export interface SketchData { v: number; units: 'feet'; members: SketchItem[]; measurements: SketchMeasurement[] }

const isSheet = (m: SketchItem): m is SheetItem => m.kind === 'sheet';
const rid = () => Math.random().toString(36).slice(2, 10);
const emptySketch = (): SketchData => ({ v: 3, units: 'feet', members: [], measurements: [] });

type SketchMode = 'off' | 'sketch' | 'measure' | 'cut' | 'cutout' | 'lay' | 'line' | 'layout' | 'layrun' | 'generate';
type Tool = 'none' | 'cut' | 'cutout' | 'lay' | 'line' | 'layout';

// Wave 5: layout-tool config (children butted end-grain onto a face of the
// base member, spaced on centres, tilted to a pitch about the base's axis).
interface LayoutCfg { size: LumberSizeKey; spacingIn: number; startIn: number; count: number | null; lengthIn: number; pitchN: number; face: number }
const FACE_LABELS = ['Wide A', 'Wide B', 'Edge A', 'Edge B'];

// Wave 5: lay-run tool config (boards laid flush between two tapped points).
interface RunCfg { mode: 'single' | 'fill' | 'oc'; gapIn: number; spacingIn: number; size: LumberSizeKey; lenIn: number | null; flat: boolean }

// Wave 7: "Generate" menu — tap-assisted roof-framing generators. The user
// taps a roof plane (ladder: then the rake edge); the plane's extent is
// recovered from the model's coplanar triangles and a preview is offered.
type GenKind = 'sheath' | 'purlin' | 'fascia' | 'ladder';
interface GenCfg {
  kind: GenKind;
  plyThickIn: number; plyStagger: boolean; plyGapIn: number;
  purlinSize: LumberSizeKey; purlinSpacingIn: number; purlinFlat: boolean;
  fasciaSize: LumberSizeKey;
  ladderSize: LumberSizeKey; ladderSpacingIn: number; ladderOverhangIn: number; ladderInboardIn: number; ladderStartIn: number;
}
const STOCK_MAX_IN = 192; // 16' max stock — longer runs get plain butt splices

const HELP_SEEN_KEY = 'mmdlViewerHelpSeen';

// Rendered inside the engine (first-run overlay) AND by hosts on their own
// Help tabs. `variant` only swaps host-specific wording — the tool content
// is identical because the toolkit is identical.
export type HelpVariant = 'mmdl' | 'drafting';

export function HelpContent({ onDone, variant = 'mmdl' }: { onDone?: () => void; variant?: HelpVariant }) {
  const modelNoun = variant === 'drafting' ? 'building model' : 'truss model';
  return (
    <div className="max-w-2xl mx-auto py-6 px-4 space-y-6 overflow-y-auto">
      <div className="text-center">
        <span className="material-symbols-outlined text-5xl text-primary">view_in_ar</span>
        <h3 className="text-xl font-bold text-gray-900 mt-1">How to view the 3D model</h3>
        <p className="text-sm text-gray-500 mt-1">Move around the building just like turning a model in your hands.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h4 className="flex items-center gap-2 font-bold text-gray-900 mb-3">
            <span className="material-symbols-outlined text-primary">touch_app</span> On a phone or tablet
          </h4>
          <ul className="space-y-2.5 text-sm text-gray-700">
            <li className="flex gap-2"><span className="material-symbols-outlined text-base text-gray-400">swipe</span> Drag with <strong>one finger</strong> to spin the model</li>
            <li className="flex gap-2"><span className="material-symbols-outlined text-base text-gray-400">pinch</span> <strong>Pinch</strong> with two fingers to zoom in and out</li>
            <li className="flex gap-2"><span className="material-symbols-outlined text-base text-gray-400">drag_pan</span> Drag with <strong>two fingers</strong> to slide the view</li>
            <li className="flex gap-2"><span className="material-symbols-outlined text-base text-gray-400">touch_app</span> <strong>Tap any part</strong> to see its label (e.g. truss A01)</li>
          </ul>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h4 className="flex items-center gap-2 font-bold text-gray-900 mb-3">
            <span className="material-symbols-outlined text-primary">mouse</span> With a mouse
          </h4>
          <ul className="space-y-2.5 text-sm text-gray-700">
            <li className="flex gap-2"><span className="material-symbols-outlined text-base text-gray-400">drag_click</span> Hold the <strong>left button</strong> and drag to spin</li>
            <li className="flex gap-2"><span className="material-symbols-outlined text-base text-gray-400">mouse</span> <strong>Scroll wheel</strong> to zoom in and out</li>
            <li className="flex gap-2"><span className="material-symbols-outlined text-base text-gray-400">drag_pan</span> Hold the <strong>right button</strong> and drag to slide</li>
            <li className="flex gap-2"><span className="material-symbols-outlined text-base text-gray-400">left_click</span> <strong>Click any part</strong> to see its label</li>
          </ul>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h4 className="flex items-center gap-2 font-bold text-gray-900 mb-2">
          <span className="material-symbols-outlined text-primary">smart_button</span> The buttons
        </h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li><strong>Fit</strong> — brings the whole building back into view (use this if you get lost)</li>
          <li><strong>Top</strong> — looks straight down, like the paper plan</li>
          <li><strong>Front</strong> — looks from the side, like standing in the yard</li>
          <li><strong>+ / −</strong> — zoom without pinching or scrolling</li>
          {variant === 'mmdl'
            ? <li><strong>Trusses / Walls / Beams</strong> — hide or show parts. Hiding walls makes the trusses easier to see.</li>
            : <li><strong>Walls / Floors / Roof / Site</strong> — hide or show parts of the building. Hiding the roof makes the rooms easier to see.</li>}
        </ul>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h4 className="flex items-center gap-2 font-bold text-gray-900 mb-2">
          <span className="material-symbols-outlined text-primary">carpenter</span> Sketching — work out framing right in the model
        </h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li><strong>Sketch</strong> — turns the framing tools on. Pick a size (2x4 … 6x6, or Plywood), type a length like <strong>10' 6"</strong>, and hit <strong>Add</strong> — the piece drops in at the middle of your view with its true dressed size.</li>
          <li><strong>Tap a piece</strong> to pick it up — then drag it to slide it around, use the arrows to nudge it (1/4", 1", 6" or 12" a tap), turn it 90°, change its length, copy it, or delete it.</li>
          <li><strong>Precise</strong> — opens exact controls for the picked piece: type its position in feet-inches (X / Y-up / Z from the model origin) or its angles in degrees, and watch the live position read-out while you drag. <strong>Snap 15°</strong> makes the fine-turn buttons land on clean 15° steps.</li>
          <li><strong>Up-down drag</strong> (the ↕ button) — dragging raises or lowers the piece instead of sliding it across the ground. Tap it again to go back to sliding.</li>
          <li><strong>Autocut</strong> — with a piece picked, finds everything running through it: your other sketch pieces <strong>and the {modelNoun} itself</strong>. On a board the colliding stretch turns red — remove it and the board is cut square at both sides, tight to whatever crosses it. On plywood an opening is punched around each crossing piece or model member with 1/8" of clearance — push a sheet through a {variant === 'drafting' ? 'wall' : 'truss chord'} and it cuts itself a clean opening. Hidden layers (say, walls you've switched off) are left out of the check.</li>
          <li><strong>Cut</strong> — with a board picked, set a <strong>mitre and/or bevel</strong> (0–60°, presets like 22.5° and 45°) and tap where the saw lands — the board splits there, angles and all. Angled boards measure <strong>long point to long point</strong>, just like on the saw. On plywood, tap <strong>two points</strong> and the cut runs along that line — straight or at any angle.</li>
          <li><strong>Cut-out</strong> (plywood) — tap two opposite corners on the sheet and that rectangle is cut out — an opening in the middle, or a notch if it hangs over an edge.</li>
          <li><strong>Lay</strong> — tap a roof slope, wall, or another piece and the picked piece lays flat on that surface (plywood flat on the rafters in one tap).</li>
          <li><strong>Line</strong> — tap two points (say, both ends of a hip) and the board runs between them; with <strong>Auto-length</strong> on it stretches to exactly that span.</li>
          <li><strong>Layout</strong> — with a board picked, lays out new pieces along it: pick their size, spacing (16" / 19.2" / 24" o.c. or custom), start offset, length, a <strong>pitch as rise-per-12</strong> (0 = square off the face), and which face they butt onto (it highlights on the board). Hit <strong>Generate</strong> — studs, lookouts or rafters drop in end-grain to the face, centres at the spacing, and behave as ordinary pieces after.</li>
          <li><strong>Lay-run</strong> — tap two points on any surface and boards lay flush between them: a single board along the line, boards packed <strong>side by side</strong> with a gap, or boards at an <strong>on-centre spacing</strong> across the run — decking or strapping in two taps.</li>
          <li><strong>Generate</strong> — tap a roof plane and whole assemblies build themselves onto it, with a preview and a count before anything lands: <strong>Sheathing</strong> (4'×8' plywood, rows staggered 4', 1/8" gaps, edge sheets cut to the plane — nothing over a full sheet), <strong>Purlins</strong> (across the slope at 16"/19.2"/24" o.c., flat or on edge, butt-spliced at 16'), <strong>Sub fascia</strong> (a plumb board on edge along the eave), and a <strong>Gable ladder</strong> (tap the plane, then the rake: lookouts run flat across the drop gable out to a fly rafter at your overhang). Every generation is one piece of undo — Ctrl+Z takes the whole thing back.</li>
          <li><strong>Glide</strong> — a drag mode where the piece rides flush across whatever surface is under your finger (slide a board along the truss plane). <strong>Magnet</strong> snaps a dragged piece flush onto a nearby piece's face — within 2" it clicks tight and the face lights up.</li>
          <li><strong>Measure</strong> — tap any two points (model or your pieces) and a tag shows the distance in feet-inches. Tap a tag to remove it.</li>
          <li>Everything <strong>saves by itself</strong> — the whole crew sees the same sketch on this model.</li>
          {variant === 'drafting' && (
            <li><strong>Where the model comes from</strong> — it is your drawn plan, extruded: every wall at its true thickness and the level's wall height, doors and windows punched through, plus the simple massing roof if you turn one on. Change the plan in 2D and the model rebuilds when you come back to 3D — your sketch pieces stay exactly where you left them.</li>
          )}
        </ul>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h4 className="flex items-center gap-2 font-bold text-gray-900 mb-2">
          <span className="material-symbols-outlined text-primary">keyboard</span> Keyboard shortcuts — sketch fast on a desktop
        </h4>
        <p className="text-sm text-gray-700 mb-3">
          With <strong>Sketch</strong> on, the whole tool works from the keyboard. Press <strong>?</strong> (or <strong>K</strong>) in the
          model to pop this list up any time, and <strong>Ctrl+Z</strong> undoes any sketch change — adds, moves, cuts, the lot.
        </p>
        <ShortcutList />
      </div>

      {onDone && (
        <div className="text-center pb-2">
          <button onClick={onDone} className="h-11 px-8 rounded-xl bg-primary text-white text-base font-bold hover:opacity-90">
            Got it — show me the model
          </button>
        </div>
      )}
    </div>
  );
}

// Wave 4: what the "Autocut collisions" pass found / did (drives the chip UI).
type AutocutState =
  | null
  | { kind: 'none' }
  | { kind: 'lumber'; segments: number; removeIn: number }
  | { kind: 'sheet'; made: number; skipped: number };

interface SketchApi {
  addLumber: (size: LumberSizeKey, lengthIn: number) => void;
  addSheet: (thickIn: number, widthIn: number, lengthIn: number) => void;
  select: (id: string | null) => void;
  nudge: (axis: 'x' | 'y' | 'z', dir: 1 | -1, stepIn: number) => void;
  rotate: (axis: 'x' | 'y' | 'z', deg: number, snap15?: boolean) => void;
  setPosition: (axis: 'x' | 'y' | 'z', valueFt: number) => void;
  setRotationDeg: (axis: 'x' | 'y' | 'z', deg: number) => void;
  autocutRun: () => void;
  autocutConfirm: () => void;
  autocutCancel: () => void;
  setLength: (lengthIn: number) => void;
  setWidth: (widthIn: number) => void;
  setThick: (thickIn: number) => void;
  duplicate: () => void;
  remove: () => void;
  clearMeasurements: () => void;
  cancelMeasure: () => void;
  setCutAngles: (miter: number, bevel: number) => void;
  confirmCut: () => void;
  cancelCut: () => void;
  undoCutout: () => void;
  layoutGenerate: (cfg: LayoutCfg) => void;
  setLayoutFace: (face: number | null) => void;
  // --- Wave 6: keyboard layer ---
  nudgeCam: (rightSteps: number, fwdSteps: number, stepIn: number) => void;
  yawWorld: (deg: number, snap?: boolean) => void;
  restoreSketch: (data: SketchData) => void;
  // --- Wave 7: Generate menu ---
  genConfirm: () => void;
  genCancel: () => void;
}

// Wave 6: every keyboard binding in plain English — rendered in the "?"
// cheat-sheet overlay and on the Help tab. Active in sketch mode only.
const KEY_SHORTCUTS: { group: string; keys: [string, string][] }[] = [
  { group: 'General', keys: [
    ['? or K', 'Show or hide the shortcut list'],
    ['Esc', 'One step back: close the list, else cancel the active tool, else put the piece down'],
    ['Tab / Shift+Tab', 'Step through your pieces one by one (the camera stays put)'],
    ['Ctrl+Z', 'Undo the last sketch change'],
    ['Ctrl+Shift+Z or Ctrl+Y', 'Redo it again'],
    ['Ctrl+S', 'Save right now (it also saves by itself)'],
    ['Ctrl+D', 'Copy the picked piece'],
    ['Delete or Backspace', 'Delete the picked piece'],
    ['X', 'Turn Sketch mode off'],
  ] },
  { group: 'Move the picked piece', keys: [
    ['Arrow keys', 'Slide it across the ground the way you are looking — up slides it away from you'],
    ['Q / E (or Page Up / Down)', 'Raise / lower it'],
    ['Shift + a move key', 'Big jump — 12 steps at once'],
    ['Alt + a move key', 'Hair-fine — 1/16" a tap'],
    ['[ and ]', 'Smaller / bigger step (1/16" · 1/4" · 1" · 6" · 12")'],
  ] },
  { group: 'Turn the picked piece', keys: [
    [', and .', 'Spin it flat, like turning a board on the deck (Snap 15° applies)'],
    ["; and '", 'Roll it over about its own length'],
    ['Shift + a turn key', 'Quarter turn — 90°'],
    ['Alt + a turn key', 'Fine turn — 1°'],
  ] },
  { group: 'Tools', keys: [
    ['B then Enter', 'Add lumber — B jumps to the length box, Enter drops the piece in'],
    ['P then Enter', 'Add plywood the same way'],
    ['1 – 7', 'Pick the size: 2x4, 2x6, 2x8, 2x10, 2x12, 4x4, 6x6'],
    ['M', 'Measure'],
    ['C', 'Cut the picked piece'],
    ['O', 'Cut-out (plywood)'],
    ['L', 'Lay on a surface'],
    ['N', 'Line — run between two points'],
    ['Y', 'Layout along the picked board'],
    ['R', 'Lay-run'],
    ['A', 'Autocut the picked piece'],
    ['G / V / T', 'Glide drag · up-down drag · Magnet on and off'],
  ] },
];

function ShortcutList({ compact }: { compact?: boolean }) {
  return (
    <div className={`grid sm:grid-cols-2 ${compact ? 'gap-x-6 gap-y-3' : 'gap-4'}`}>
      {KEY_SHORTCUTS.map(g => (
        <div key={g.group}>
          <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1">{g.group}</div>
          <ul className="space-y-1">
            {g.keys.map(([k, what]) => (
              <li key={k} className="flex items-start gap-2 text-xs text-gray-700">
                <span className="shrink-0 font-mono font-bold text-[11px] bg-gray-100 border border-gray-300 rounded px-1.5 py-0.5 text-gray-800">{k}</span>
                <span className="pt-0.5">{what}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function SketchEngine3D({ active, modelKey, loadModel, layers, adapter, adapterKey, onError, helpVariant = 'mmdl' }: {
  active: boolean;                              // build the scene while true; dispose when false
  modelKey: string;                             // scene rebuilds when this changes
  loadModel: () => Promise<EngineObject[]>;     // passive model, Z-up feet
  layers: EngineLayer[];
  adapter?: SketchAdapter;                      // sketch persistence; omit = sketching without save
  adapterKey?: string | number;                 // sketch identity (reload when it changes)
  onError?: (message: string) => void;          // model failed to load/build
  helpVariant?: HelpVariant;
}) {
  const [firstHelp, setFirstHelp] = useState(() => !localStorage.getItem(HELP_SEEN_KEY));
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<{ type: string; name: string } | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // --- Sketch state ---
  const [sketchOn, setSketchOn] = useState(false);
  const [measureOn, setMeasureOn] = useState(false);
  const [tool, setTool] = useState<Tool>('none');
  const [selected, setSelected] = useState<SketchItem | null>(null);
  const [measureCount, setMeasureCount] = useState(0);
  const [saveState, setSaveState] = useState<'clean' | 'dirty' | 'saving' | 'saved' | 'error'>('clean');
  const [addKind, setAddKind] = useState<LumberSizeKey | 'ply'>('2x4');
  const [addLen, setAddLen] = useState(`8' 0"`);
  const [addWidth, setAddWidth] = useState(`4' 0"`);
  const [addThick, setAddThick] = useState(0.5);
  const [step, setStep] = useState(1);
  const [lenDraft, setLenDraft] = useState('');
  const [widthDraft, setWidthDraft] = useState('');
  const [cutPending, setCutPending] = useState<{ kind: 'lumber'; uIn: number } | { kind: 'sheet'; angleDeg: number } | null>(null);
  const [cutoutStage, setCutoutStage] = useState<0 | 1>(0);
  const [cutStage, setCutStage] = useState<0 | 1>(0); // sheet line cut: how many points tapped
  const [cutMiter, setCutMiter] = useState(0);
  const [cutBevel, setCutBevel] = useState(0);
  // --- Wave 4: precision control + autocut ---
  const [preciseOpen, setPreciseOpen] = useState(false);
  const [posDraft, setPosDraft] = useState<[string, string, string]>(['', '', '']);
  const [rotDraft, setRotDraft] = useState<[string, string, string]>(['', '', '']);
  const [livePos, setLivePos] = useState<V3 | null>(null);
  const [snap15, setSnap15] = useState(false);
  const [vertDrag, setVertDrag] = useState(false);
  const vertDragRef = useRef(false);
  useEffect(() => { vertDragRef.current = vertDrag; }, [vertDrag]);
  const [autocut, setAutocut] = useState<AutocutState>(null);
  // --- Wave 5: glide drag, magnet face-snap, layout tool, lay-run tool ---
  const [glide, setGlide] = useState(false);
  const glideRef = useRef(false);
  useEffect(() => { glideRef.current = glide; }, [glide]);
  const [magnet, setMagnet] = useState(true);
  const magnetRef = useRef(true);
  useEffect(() => { magnetRef.current = magnet; }, [magnet]);
  const [laySize, setLaySize] = useState<LumberSizeKey>('2x4');
  const [laySpacing, setLaySpacing] = useState('24');
  const [laySpacingCustom, setLaySpacingCustom] = useState('12"');
  const [layStart, setLayStart] = useState('0"');
  const [layAuto, setLayAuto] = useState(true);
  const [layCount, setLayCount] = useState('4');
  const [layLen, setLayLen] = useState(`4' 0"`);
  const [layPitch, setLayPitch] = useState('0');
  const [layFace, setLayFace] = useState(0);
  const [runOn, setRunOn] = useState(false);
  const [runStage, setRunStage] = useState<0 | 1>(0);
  const [runMode, setRunMode] = useState<'single' | 'fill' | 'oc'>('single');
  const [runGap, setRunGap] = useState('1/8"');
  const [runSpacing, setRunSpacing] = useState('16"');
  const [runSize, setRunSize] = useState<LumberSizeKey>('2x4');
  const [runLen, setRunLen] = useState('');
  const [runFlat, setRunFlat] = useState(true);
  // --- Wave 7: Generate menu (roof-plane generators) ---
  const [genOn, setGenOn] = useState(false);
  const [genKind, setGenKind] = useState<GenKind>('sheath');
  const [genPlyThick, setGenPlyThick] = useState(0.5);
  const [genPlyStagger, setGenPlyStagger] = useState(true);
  const [genPurlinSize, setGenPurlinSize] = useState<LumberSizeKey>('2x4');
  const [genPurlinSpacing, setGenPurlinSpacing] = useState('24"');
  const [genPurlinFlat, setGenPurlinFlat] = useState(true);
  const [genFasciaSize, setGenFasciaSize] = useState<LumberSizeKey>('2x6');
  const [genLadderSize, setGenLadderSize] = useState<LumberSizeKey>('2x4');
  const [genLadderSpacing, setGenLadderSpacing] = useState('24"');
  const [genLadderOverhang, setGenLadderOverhang] = useState('24"');
  const [genLadderInboard, setGenLadderInboard] = useState(`4' 0"`);
  const [genLadderStart, setGenLadderStart] = useState('0"');
  const [genStage, setGenStage] = useState<0 | 1>(0);
  const [genPlan, setGenPlan] = useState<{ label: string; ok: boolean } | null>(null);
  const genCfgRef = useRef<GenCfg>({
    kind: 'sheath', plyThickIn: 0.5, plyStagger: true, plyGapIn: 0.125,
    purlinSize: '2x4', purlinSpacingIn: 24, purlinFlat: true, fasciaSize: '2x6',
    ladderSize: '2x4', ladderSpacingIn: 24, ladderOverhangIn: 24, ladderInboardIn: 48, ladderStartIn: 0,
  });
  useEffect(() => {
    const num = (s: string, dflt: number, lo: number, hi: number) => {
      const v = s.trim() ? parseFeetInches(s) : null;
      return v === null ? dflt : Math.max(lo, Math.min(hi, v));
    };
    genCfgRef.current = {
      kind: genKind,
      plyThickIn: genPlyThick, plyStagger: genPlyStagger, plyGapIn: 0.125,
      purlinSize: genPurlinSize, purlinSpacingIn: num(genPurlinSpacing, 24, 4, 96), purlinFlat: genPurlinFlat,
      fasciaSize: genFasciaSize,
      ladderSize: genLadderSize, ladderSpacingIn: num(genLadderSpacing, 24, 4, 96),
      ladderOverhangIn: num(genLadderOverhang, 24, 2, 96), ladderInboardIn: num(genLadderInboard, 48, 6, 192),
      ladderStartIn: num(genLadderStart, 0, 0, 192),
    };
  }, [genKind, genPlyThick, genPlyStagger, genPurlinSize, genPurlinSpacing, genPurlinFlat, genFasciaSize,
      genLadderSize, genLadderSpacing, genLadderOverhang, genLadderInboard, genLadderStart]);
  // Switching generator type drops any half-done tap sequence or preview.
  useEffect(() => { api.current?.sketch.genCancel(); }, [genKind]);
  // --- Wave 6: keyboard layer + global undo ---
  const [cheatOpen, setCheatOpen] = useState(false);
  const [isDesktop] = useState(() => typeof window !== 'undefined'
    && (window.matchMedia('(hover: hover) and (pointer: fine)').matches || navigator.maxTouchPoints === 0));
  const runCfgRef = useRef<RunCfg>({ mode: 'single', gapIn: 0.125, spacingIn: 16, size: '2x4', lenIn: null, flat: true });
  useEffect(() => {
    runCfgRef.current = {
      mode: runMode,
      gapIn: Math.max(0, runGap.trim() ? (parseFeetInches(runGap) ?? 0.125) : 0.125),
      spacingIn: Math.max(1, runSpacing.trim() ? (parseFeetInches(runSpacing) ?? 16) : 16),
      size: runSize,
      lenIn: runLen.trim() ? parseFeetInches(runLen) : null,
      flat: runFlat,
    };
  }, [runMode, runGap, runSpacing, runSize, runLen, runFlat]);

  const mountRef = useRef<HTMLDivElement>(null);
  const sketchRef = useRef<SketchData>(emptySketch());
  const sketchLoadedFor = useRef<string | number | null>(null);
  // Latest persistence adapter — refs so the unmount flush and the debounced
  // save never capture a stale closure.
  const adapterRef = useRef(adapter);
  useEffect(() => { adapterRef.current = adapter; }, [adapter]);
  const modeRef = useRef<SketchMode>('off');
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Imperative bridge into the three.js scene (set once the scene builds).
  const api = useRef<{
    fit: () => void; top: () => void; front: () => void; zoom: (f: number) => void;
    setVisible: (type: string, visible: boolean) => void; dispose: () => void;
    sketch: SketchApi;
  } | null>(null);

  const dismissFirstHelp = () => { localStorage.setItem(HELP_SEEN_KEY, '1'); setFirstHelp(false); };

  // Debounced shared autosave (~2s after the last change).
  const scheduleSave = () => {
    if (!adapterRef.current) return;
    setSaveState('dirty');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      saveTimer.current = null;
      setSaveState('saving');
      try {
        await adapterRef.current!.save(sketchRef.current);
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, 2000);
  };

  // --- Wave 6: global sketch undo/redo. Session-local, ~40 entries; a
  // snapshot of the sketch JSON is pushed BEFORE every mutating action (see
  // the mutate() wrapper in the scene closure). Undo/redo restores the whole
  // sketch and autosaves.
  const undoStack = useRef<SketchData[]>([]);
  const redoStack = useRef<SketchData[]>([]);
  const cloneSketch = (): SketchData => JSON.parse(JSON.stringify(sketchRef.current)) as SketchData;
  const pushUndo = (snap: SketchData) => {
    undoStack.current.push(snap);
    if (undoStack.current.length > 40) undoStack.current.shift();
    redoStack.current = []; // a fresh mutation invalidates the redo branch
  };
  const doUndo = () => {
    const snap = undoStack.current.pop();
    if (!snap || !api.current) return;
    redoStack.current.push(cloneSketch());
    api.current.sketch.restoreSketch(snap);
  };
  const doRedo = () => {
    const snap = redoStack.current.pop();
    if (!snap || !api.current) return;
    undoStack.current.push(cloneSketch()); // direct push — redo must survive
    api.current.sketch.restoreSketch(snap);
  };

  // Ctrl/Cmd+S: skip the debounce and save immediately.
  const forceSave = async () => {
    if (!adapterRef.current) return;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setSaveState('saving');
    try {
      await adapterRef.current.save(sketchRef.current);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  };

  // Flush a pending save if the engine unmounts mid-debounce.
  useEffect(() => () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      adapterRef.current?.save(sketchRef.current).catch(() => {});
    }
  }, []);

  // Keep the scene-side mode flag in sync with the UI state.
  useEffect(() => {
    const mode: SketchMode = !sketchOn ? 'off' : measureOn ? 'measure' : runOn ? 'layrun' : genOn ? 'generate' : (tool !== 'none' && selected) ? tool : 'sketch';
    modeRef.current = mode;
    if (mode !== 'cut' && mode !== 'cutout' && mode !== 'lay' && mode !== 'line' && mode !== 'layrun') api.current?.sketch.cancelCut();
    if (mode !== 'measure') api.current?.sketch.cancelMeasure();
    if (mode !== 'sketch') api.current?.sketch.autocutCancel();
    if (mode !== 'generate') api.current?.sketch.genCancel();
    if (!selected && tool !== 'none') setTool('none');
  }, [sketchOn, measureOn, runOn, genOn, tool, selected]);

  // Wave 5: highlight the layout target face on the base member.
  useEffect(() => {
    if (sketchOn && tool === 'layout' && selected && selected.kind !== 'sheet') api.current?.sketch.setLayoutFace(layFace);
    else api.current?.sketch.setLayoutFace(null);
  }, [sketchOn, tool, layFace, selected]);

  // Push the pending-cut angles into the scene (drives the preview plane).
  useEffect(() => {
    api.current?.sketch.setCutAngles(cutMiter, cutBevel);
  }, [cutMiter, cutBevel, loading]);

  // Auto-length flag for the "Run along line" snap (scene reads the ref).
  const [autoLen, setAutoLen] = useState(true);
  const autoLenRef = useRef(true);
  useEffect(() => { autoLenRef.current = autoLen; }, [autoLen]);
  const [snapStage, setSnapStage] = useState<0 | 1>(0); // line snap: points tapped

  // Mirror the selected item's dimensions/position/rotation into the editors.
  useEffect(() => {
    if (!selected) { setLivePos(null); return; }
    setLenDraft(formatInches(selected.lengthIn));
    if (selected.kind === 'sheet') setWidthDraft(formatInches(selected.widthIn));
    setPosDraft([formatSignedFeet(selected.pos[0]), formatSignedFeet(selected.pos[1]), formatSignedFeet(selected.pos[2])]);
    setRotDraft(selected.rot.map(r => String(Math.round(((r * 180) / Math.PI) * 10) / 10)) as [string, string, string]);
    setLivePos(selected.pos);
  }, [selected]);

  // Build the 3D scene
  useEffect(() => {
    if (!active) return;
    let disposed = false;
    (async () => {
      try {
        setLoading(true);
        const [loadedObjects, THREE, { OrbitControls }, { mergeGeometries }] = await Promise.all([
          loadModel(),
          import('three'),
          import('three/examples/jsm/controls/OrbitControls.js'),
          import('three/examples/jsm/utils/BufferGeometryUtils.js'),
        ]);
        // Load the persisted sketch once per adapterKey (re-activations and
        // model rebuilds keep local state).
        const skKey = adapterKey ?? 'default';
        if (adapter && sketchLoadedFor.current !== skKey) {
          try {
            const d = (await adapter.load()) as SketchData | null;
            if (d && Array.isArray(d.members)) {
              sketchRef.current = {
                v: 3, units: 'feet',
                members: d.members.filter(m => m && m.id).map(m =>
                  m.kind === 'sheet'
                    ? { ...m, cuts: Array.isArray(m.cuts) ? m.cuts : [] }
                    : { ...m, kind: 'lumber' as const }
                ),
                measurements: Array.isArray(d.measurements) ? d.measurements : [],
              };
            }
          } catch { /* sketch stays empty; still allow sketching */ }
          sketchLoadedFor.current = skKey;
        }
        const objects: MmdlObject[] = loadedObjects;
        const mount = mountRef.current;
        if (!mount || disposed) return;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf5f1ea);
        const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.5, 5000);
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        mount.appendChild(renderer.domElement);

        scene.add(new THREE.AmbientLight(0xffffff, 0.75));
        const sun = new THREE.DirectionalLight(0xffffff, 1.1);
        sun.position.set(1, 2, 1.2);
        scene.add(sun);
        const fill = new THREE.DirectionalLight(0xffffff, 0.35);
        fill.position.set(-1.2, -0.6, -1);
        scene.add(fill);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.maxPolarAngle = Math.PI * 0.55; // keep users from flipping under the floor

        // Build meshes — file is Z-up feet; three is Y-up: (x, y, z) → (x, z, -y)
        const groupByType = new Map<string, import('three').Group>();
        const pickables: import('three').Mesh[] = [];
        for (const obj of objects) {
          let grp = groupByType.get(obj.type);
          if (!grp) { grp = new THREE.Group(); groupByType.set(obj.type, grp); scene.add(grp); }
          const pos: number[] = [];
          const col: number[] = [];
          const idx: number[] = [];
          let minA = 255;
          let base = 0;
          for (const m of obj.meshes) {
            const [cr, cg, cb, ca] = m.color;
            minA = Math.min(minA, ca);
            const n = m.positions.length / 3;
            for (let v = 0; v < n; v++) {
              const x = m.positions[v * 3], y = m.positions[v * 3 + 1], z = m.positions[v * 3 + 2];
              pos.push(x, z, -y);
              col.push(cr / 255, cg / 255, cb / 255);
            }
            for (const i of m.triangles) idx.push(base + i);
            base += n;
          }
          if (idx.length === 0) continue;
          const geo = new THREE.BufferGeometry();
          geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
          geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
          geo.setIndex(idx);
          geo.computeVertexNormals();
          const translucent = minA < 240;
          const mat = new THREE.MeshLambertMaterial({
            vertexColors: true,
            side: THREE.DoubleSide,
            transparent: translucent,
            opacity: translucent ? Math.max(minA / 255, 0.35) : 1,
          });
          const mesh = new THREE.Mesh(geo, mat);
          mesh.userData = { type: obj.type, name: obj.name };
          grp.add(mesh);
          pickables.push(mesh);
          // Crisp outlines make framing much easier to read
          const edges = new THREE.LineSegments(
            new THREE.EdgesGeometry(geo, 30),
            new THREE.LineBasicMaterial({ color: 0x4a4038, transparent: true, opacity: translucent ? 0.25 : 0.55 })
          );
          mesh.add(edges);
        }

        // Frame the model (before the sketch layer so stray pieces don't skew it)
        const box = new THREE.Box3().setFromObject(scene);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) || 10;
        const grid = new THREE.GridHelper(maxDim * 2.2, 22, 0xb8a68f, 0xded2c2);
        grid.position.set(center.x, box.min.y - 0.05, center.z);
        scene.add(grid);

        const setCam = (dx: number, dy: number, dz: number) => {
          camera.position.set(center.x + dx, center.y + dy, center.z + dz);
          controls.target.copy(center);
          controls.update();
        };
        const fit = () => setCam(maxDim * 0.85, maxDim * 0.65, maxDim * 0.95);
        const top = () => setCam(0, maxDim * 1.6, 0.01);
        const front = () => setCam(0, size.y * 0.25, maxDim * 1.5);
        fit();

        // ================= Sketch layer =================
        const WOOD = 0xE3C285, WOOD_EDGE = 0x8a6b42, PLY = 0xF2E4BB, PLY_EDGE = 0xa98f5f, SEL = 0xC77D3F, TAPE = 0xB8462E;
        const sketchGroup = new THREE.Group();
        const measureGroup = new THREE.Group();
        scene.add(sketchGroup);
        scene.add(measureGroup);
        const itemMeshes = new Map<string, import('three').Mesh>();
        let selectedId: string | null = null;

        // HTML layer for measurement tags
        const labelLayer = document.createElement('div');
        labelLayer.style.cssText = 'position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:6;';
        mount.appendChild(labelLayer);
        const measureLabels = new Map<string, { el: HTMLButtonElement; mid: import('three').Vector3 }>();

        const itemById = (id: string | null) => sketchRef.current.members.find(m => m.id === id);

        // Wave 6: every mutating action funnels through mutate() so a
        // pre-mutation snapshot of the sketch JSON lands on the undo stack.
        // Nested calls (e.g. "Clear tape" removing each measurement) collapse
        // into one undo entry.
        let mutDepth = 0;
        const mutate = <T,>(fn: () => T): T => {
          const snap = mutDepth === 0 ? cloneSketch() : null;
          mutDepth++;
          try { return fn(); } finally {
            mutDepth--;
            if (snap) pushUndo(snap);
          }
        };

        const buildItemGeometry = (item: SketchItem): import('three').BufferGeometry => {
          if (isSheet(item)) {
            const Lft = item.lengthIn / 12, Wft = item.widthIn / 12, Tft = item.thickIn / 12;
            let geo: import('three').BufferGeometry;
            if (item.poly) {
              // Angle-cut sheet: extrude its polygon outline; rectangular
              // cut-outs render only when fully inside the outline.
              const shape = new THREE.Shape(item.poly.map(p => new THREE.Vector2(p[0] / 12, p[1] / 12)));
              for (const c of item.cuts) {
                const cs: Pt[] = [[c.x, c.y], [c.x + c.w, c.y], [c.x + c.w, c.y + c.h], [c.x, c.y + c.h]];
                if (cs.every(p => pointInPoly(p, item.poly!))) {
                  shape.holes.push(new THREE.Path(cs.map(p => new THREE.Vector2(p[0] / 12, p[1] / 12))));
                }
              }
              geo = new THREE.ExtrudeGeometry(shape, { depth: Tft, bevelEnabled: false });
            } else {
              const outlines = sheetOutlines(item.lengthIn, item.widthIn, item.cuts);
              const geos = outlines.map(o => {
                const shape = new THREE.Shape(o.outer.map(p => new THREE.Vector2(p[0] / 12, p[1] / 12)));
                for (const h of o.holes) shape.holes.push(new THREE.Path(h.map(p => new THREE.Vector2(p[0] / 12, p[1] / 12))));
                return new THREE.ExtrudeGeometry(shape, { depth: Tft, bevelEnabled: false });
              });
              geo = geos.length === 1 ? geos[0] : mergeGeometries(geos);
            }
            // Centre, then lay flat: length → X, width → Z, thickness → Y
            geo.translate(-Lft / 2, -Wft / 2, -Tft / 2);
            geo.rotateX(-Math.PI / 2);
            return geo;
          }
          const d = lumberDims(item.size);
          if (isSquare(item.e0) && isSquare(item.e1)) {
            return new THREE.BoxGeometry(item.lengthIn / 12, d.depth / 12, d.thick / 12);
          }
          // Mitred/bevelled end(s): convex 8-vertex solid, flat-shaded triangle soup
          const tris = angledLumberTriangles(item.lengthIn / 12, d.depth / 12, d.thick / 12, item.e0, item.e1);
          const geo = new THREE.BufferGeometry();
          geo.setAttribute('position', new THREE.Float32BufferAttribute(tris, 3));
          geo.computeVertexNormals();
          return geo;
        };

        // A piece mesh can carry extra children (autocut preview boxes, the
        // layout face highlight) — always locate its outline by type.
        const edgeOf = (mesh: import('three').Object3D) =>
          mesh.children.find((c): c is import('three').LineSegments => (c as import('three').LineSegments).isLineSegments === true);

        const setHighlight = (mesh: import('three').Mesh, on: boolean, sheet: boolean) => {
          const mat = mesh.material as import('three').MeshLambertMaterial;
          mat.emissive.setHex(on ? SEL : 0x000000);
          mat.emissiveIntensity = 0.4;
          const edge = edgeOf(mesh);
          if (edge) (edge.material as import('three').LineBasicMaterial).color.setHex(on ? SEL : (sheet ? PLY_EDGE : WOOD_EDGE));
        };

        const buildItemMesh = (item: SketchItem) => {
          const geo = buildItemGeometry(item);
          const mat = new THREE.MeshLambertMaterial({ color: isSheet(item) ? PLY : WOOD });
          const mesh = new THREE.Mesh(geo, mat);
          mesh.position.fromArray(item.pos);
          mesh.rotation.set(item.rot[0], item.rot[1], item.rot[2]);
          mesh.userData = { sketchId: item.id };
          mesh.add(new THREE.LineSegments(
            new THREE.EdgesGeometry(geo, 30),
            new THREE.LineBasicMaterial({ color: isSheet(item) ? PLY_EDGE : WOOD_EDGE })
          ));
          sketchGroup.add(mesh);
          itemMeshes.set(item.id, mesh);
          return mesh;
        };

        const rebuildItemGeometry = (item: SketchItem) => {
          const mesh = itemMeshes.get(item.id);
          if (!mesh) return;
          const oldEdge = edgeOf(mesh);
          if (oldEdge) { mesh.remove(oldEdge); oldEdge.geometry.dispose(); }
          mesh.geometry.dispose();
          const geo = buildItemGeometry(item);
          mesh.geometry = geo;
          mesh.add(new THREE.LineSegments(
            new THREE.EdgesGeometry(geo, 30),
            new THREE.LineBasicMaterial({ color: isSheet(item) ? PLY_EDGE : WOOD_EDGE })
          ));
          if (selectedId === item.id) setHighlight(mesh, true, isSheet(item));
        };

        const destroyItemMesh = (id: string) => {
          const mesh = itemMeshes.get(id);
          if (!mesh) return;
          const edge = edgeOf(mesh);
          if (edge) edge.geometry.dispose();
          mesh.geometry.dispose();
          (mesh.material as import('three').Material).dispose();
          sketchGroup.remove(mesh);
          itemMeshes.delete(id);
        };

        const selectItem = (id: string | null) => {
          clearPendingCut();
          clearAutocut();
          if (selectedId) {
            const pm = itemMeshes.get(selectedId);
            const pit = itemById(selectedId);
            if (pm) setHighlight(pm, false, pit ? isSheet(pit) : false);
          }
          const it = id ? itemById(id) : undefined;
          if (id && it) {
            selectedId = id;
            const mesh = itemMeshes.get(id);
            if (mesh) setHighlight(mesh, true, isSheet(it));
            setSelected({ ...it });
          } else {
            selectedId = null;
            setSelected(null);
          }
        };

        // --- Measurements ---
        const dotGeo = new THREE.SphereGeometry(Math.max(maxDim * 0.004, 0.05), 12, 12);
        const dotMat = new THREE.MeshBasicMaterial({ color: TAPE, depthTest: false });

        const removeMeasurement = (id: string) => mutate(() => {
          sketchRef.current.measurements = sketchRef.current.measurements.filter(m => m.id !== id);
          const g = measureGroup.children.find(c => c.userData.measureId === id);
          if (g) measureGroup.remove(g);
          const lbl = measureLabels.get(id);
          if (lbl) { lbl.el.remove(); measureLabels.delete(id); }
          setMeasureCount(sketchRef.current.measurements.length);
          scheduleSave();
        });

        const buildMeasurement = (mm: SketchMeasurement) => {
          const a = new THREE.Vector3().fromArray(mm.a), b = new THREE.Vector3().fromArray(mm.b);
          const g = new THREE.Group();
          g.userData = { measureId: mm.id };
          const line = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([a, b]),
            new THREE.LineBasicMaterial({ color: TAPE, depthTest: false })
          );
          line.renderOrder = 998;
          g.add(line);
          for (const p of [a, b]) {
            const s = new THREE.Mesh(dotGeo, dotMat);
            s.renderOrder = 999;
            s.position.copy(p);
            g.add(s);
          }
          measureGroup.add(g);
          const el = document.createElement('button');
          el.textContent = formatFeet(a.distanceTo(b));
          el.style.cssText = 'position:absolute;transform:translate(-50%,-140%);pointer-events:auto;background:#B8462E;color:#fff;font:700 12px Manrope,sans-serif;padding:7px 10px;border-radius:8px;box-shadow:0 1px 4px rgba(0,0,0,.35);white-space:nowrap;border:none;cursor:pointer;';
          el.title = 'Tap to remove this measurement';
          el.addEventListener('click', () => removeMeasurement(mm.id));
          labelLayer.appendChild(el);
          measureLabels.set(mm.id, { el, mid: a.clone().add(b).multiplyScalar(0.5) });
        };

        let measurePending: import('three').Vector3 | null = null;
        let measureMarker: import('three').Mesh | null = null;
        const clearPendingMeasure = () => {
          measurePending = null;
          if (measureMarker) { scene.remove(measureMarker); measureMarker = null; }
        };

        // --- Cuts & snaps (pending state) ---
        let pendingCut:
          | { itemId: string; type: 'lumber'; uIn: number }
          | { itemId: string; type: 'sheet'; pts: [number, number, number, number] } // u1,v1,u2,v2 (in)
          | null = null;
        let pendingAngles: EndCut = { m: 0, b: 0 };
        let cutoutFirst: { uIn: number; vIn: number } | null = null;
        let sheetCutFirst: { uIn: number; vIn: number } | null = null;
        let snapFirst: import('three').Vector3 | null = null; // "run along line" first point
        let runFirst: { p: import('three').Vector3; n: import('three').Vector3 } | null = null; // lay-run first tap
        let cutMarker: import('three').Mesh | null = null;
        let cutPreviewPlane: import('three').Mesh | null = null;
        let cutPreviewLine: import('three').Line | null = null;

        // --- Wave 4: autocut preview (red colliding segments on lumber) ---
        const autocutMat = new THREE.MeshBasicMaterial({ color: 0xd93025, transparent: true, opacity: 0.55, depthTest: false });
        let autocutPreview: import('three').Mesh[] = [];
        let autocutPlan: { itemId: string; merged: [number, number][] } | null = null;
        const clearAutocut = () => {
          for (const m of autocutPreview) { m.parent?.remove(m); m.geometry.dispose(); }
          autocutPreview = [];
          autocutPlan = null;
          setAutocut(null);
        };

        const clearPendingCut = () => {
          pendingCut = null;
          cutoutFirst = null;
          sheetCutFirst = null;
          snapFirst = null;
          runFirst = null;
          if (cutMarker) { scene.remove(cutMarker); cutMarker = null; }
          if (cutPreviewPlane) { scene.remove(cutPreviewPlane); cutPreviewPlane = null; }
          if (cutPreviewLine) { scene.remove(cutPreviewLine); cutPreviewLine.geometry.dispose(); cutPreviewLine = null; }
          setCutPending(null);
          setCutoutStage(0);
          setCutStage(0);
          setSnapStage(0);
          setRunStage(0);
        };
        const placeCutMarker = (p: import('three').Vector3) => {
          if (!cutMarker) {
            cutMarker = new THREE.Mesh(dotGeo, new THREE.MeshBasicMaterial({ color: SEL, depthTest: false }));
            cutMarker.renderOrder = 999;
            scene.add(cutMarker);
          }
          cutMarker.position.copy(p);
        };
        // Sheet-frame (u,v inches) → world, for a given sheet mesh
        const shapeToWorld = (item: SheetItem, mesh: import('three').Mesh, uIn: number, vIn: number, lift: number) =>
          mesh.localToWorld(new THREE.Vector3(
            uIn / 12 - item.lengthIn / 24,
            item.thickIn / 24 + lift,
            -(vIn / 12 - item.widthIn / 24)
          ));

        // Translucent preview of a pending mitre/bevel cut on lumber
        const updateCutPreview = () => {
          if (!pendingCut || pendingCut.type !== 'lumber') return;
          const item = itemById(pendingCut.itemId);
          const mesh = itemMeshes.get(pendingCut.itemId);
          if (!item || !mesh || isSheet(item)) return;
          const d = lumberDims(item.size);
          if (!cutPreviewPlane) {
            cutPreviewPlane = new THREE.Mesh(
              new THREE.PlaneGeometry(1, 1),
              new THREE.MeshBasicMaterial({ color: SEL, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthTest: false })
            );
            cutPreviewPlane.renderOrder = 997;
            scene.add(cutPreviewPlane);
          }
          const s = (Math.max(d.depth, d.thick) / 12) * 2.2;
          cutPreviewPlane.scale.set(s, s, 1);
          const M = (pendingAngles.m * Math.PI) / 180, B = (pendingAngles.b * Math.PI) / 180;
          const n = new THREE.Vector3(Math.cos(M) * Math.cos(B), Math.sin(M) * Math.cos(B), Math.sin(B));
          const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
          cutPreviewPlane.quaternion.copy(mesh.quaternion).multiply(q);
          cutPreviewPlane.position.copy(mesh.localToWorld(new THREE.Vector3(pendingCut.uIn / 12 - item.lengthIn / 24, 0, 0)));
        };

        // Instantiate the saved sketch
        for (const m of sketchRef.current.members) buildItemMesh(m);
        for (const mm of sketchRef.current.measurements) buildMeasurement(mm);
        setMeasureCount(sketchRef.current.measurements.length);

        // --- Pointer interaction ---
        const ray = new THREE.Raycaster();
        const pointer = new THREE.Vector2();
        const setRayFromEvent = (e: PointerEvent) => {
          const r = renderer.domElement.getBoundingClientRect();
          pointer.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
          ray.setFromCamera(pointer, camera);
        };
        const visibleHits = (targets: import('three').Object3D[]) =>
          ray.intersectObjects(targets, false).filter(h => h.object.visible && (h.object.parent?.visible ?? true));

        let downAt: [number, number] | null = null;
        let dragging = false;
        let dragVertical = false;
        let dragGlide = false; // Wave 5: glide drag — piece rides flush on the surface under the cursor
        let dragOffset = { x: 0, z: 0 };
        let dragOffsetY = 0;
        const dragPlane = new THREE.Plane();
        const dragPoint = new THREE.Vector3();

        // --- Wave 5: magnet face-snap while dragging ---
        interface SnapFace { n: import('three').Vector3; d: number; poly: import('three').Vector3[]; centre: import('three').Vector3; radius: number }
        let snapFaces: SnapFace[] = [];
        let dragVertsLocal: import('three').Vector3[] = [];
        let snapFaceMesh: import('three').Mesh | null = null;

        const hideSnapFace = () => { if (snapFaceMesh) snapFaceMesh.visible = false; };
        const showSnapFace = (f: SnapFace) => {
          const pos: number[] = [];
          const o = f.n.clone().multiplyScalar(0.004);
          for (let i = 1; i + 1 < f.poly.length; i++) {
            for (const p of [f.poly[0], f.poly[i], f.poly[i + 1]]) pos.push(p.x + o.x, p.y + o.y, p.z + o.z);
          }
          const geo = new THREE.BufferGeometry();
          geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
          if (!snapFaceMesh) {
            snapFaceMesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: SEL, transparent: true, opacity: 0.3, side: THREE.DoubleSide, depthTest: false }));
            snapFaceMesh.renderOrder = 995;
            scene.add(snapFaceMesh);
          } else {
            snapFaceMesh.geometry.dispose();
            snapFaceMesh.geometry = geo;
            snapFaceMesh.visible = true;
          }
        };

        // Cache the dragged piece's local solid and every other piece's world
        // face planes once, at drag start (they don't change during the drag).
        const beginMagnet = (item: SketchItem) => {
          snapFaces = [];
          const s = isSheet(item)
            ? sheetSolid(item.lengthIn, item.widthIn, item.thickIn, item.poly)
            : (() => { const dd = lumberDims(item.size); return lumberSolid(item.lengthIn / 12, dd.depth / 12, dd.thick / 12, item.e0, item.e1); })();
          dragVertsLocal = s.verts.map(v => new THREE.Vector3(v[0], v[1], v[2]));
          for (const other of sketchRef.current.members) {
            if (other.id === item.id) continue;
            const om = itemMeshes.get(other.id);
            if (!om) continue;
            const ws = worldSolidOf(other, om);
            const vs = ws.verts.map(v => new THREE.Vector3(v[0], v[1], v[2]));
            const centroid = new THREE.Vector3();
            for (const v of vs) centroid.add(v);
            centroid.multiplyScalar(1 / vs.length);
            for (const fIdx of ws.faces) {
              const poly = fIdx.map(i2 => vs[i2]);
              const nn = new THREE.Vector3(); // Newell normal, oriented outward
              for (let i2 = 0; i2 < poly.length; i2++) {
                const a = poly[i2], b = poly[(i2 + 1) % poly.length];
                nn.x += (a.y - b.y) * (a.z + b.z);
                nn.y += (a.z - b.z) * (a.x + b.x);
                nn.z += (a.x - b.x) * (a.y + b.y);
              }
              if (nn.lengthSq() < 1e-12) continue;
              nn.normalize();
              let dp = nn.dot(poly[0]);
              if (nn.dot(centroid) > dp) { nn.negate(); dp = -dp; }
              const centre = new THREE.Vector3();
              for (const p of poly) centre.add(p);
              centre.multiplyScalar(1 / poly.length);
              let radius = 0;
              for (const p of poly) radius = Math.max(radius, centre.distanceTo(p));
              snapFaces.push({ n: nn, d: dp, poly, centre, radius });
            }
          }
        };

        // Within ~2" of another piece's face plane (and laterally near that
        // face) → translate flush onto it and highlight the snapped face.
        const applyMagnet = (mesh: import('three').Mesh) => {
          if (!magnetRef.current || snapFaces.length === 0 || dragVertsLocal.length === 0) { hideSnapFace(); return; }
          mesh.updateMatrixWorld(true);
          const wv = dragVertsLocal.map(v => v.clone().applyMatrix4(mesh.matrixWorld));
          const c = new THREE.Vector3();
          for (const v of wv) c.add(v);
          c.multiplyScalar(1 / wv.length);
          let r = 0;
          for (const v of wv) r = Math.max(r, c.distanceTo(v));
          let best: { f: SnapFace; dmin: number } | null = null;
          for (const f of snapFaces) {
            let dmin = Infinity;
            for (const v of wv) { const t = f.n.dot(v) - f.d; if (t < dmin) dmin = t; }
            if (Math.abs(dmin) > 2 / 12) continue;
            const cp = c.clone().sub(f.n.clone().multiplyScalar(f.n.dot(c) - f.d));
            if (cp.distanceTo(f.centre) > f.radius + r) continue;
            if (!best || Math.abs(dmin) < Math.abs(best.dmin)) best = { f, dmin };
          }
          if (best) {
            mesh.position.add(best.f.n.clone().multiplyScalar(-best.dmin));
            showSnapFace(best.f);
          } else {
            hideSnapFace();
          }
        };

        const endDrag = (commit: boolean) => {
          if (!dragging) return;
          dragging = false;
          dragGlide = false;
          snapFaces = [];
          dragVertsLocal = [];
          hideSnapFace();
          controls.enabled = true;
          if (commit && selectedId) {
            const item = itemById(selectedId);
            const mesh = itemMeshes.get(selectedId);
            if (item && mesh) {
              mutate(() => {
                item.pos = mesh.position.toArray() as V3;
                item.rot = [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z]; // glide re-orients too
                setSelected({ ...item }); // refresh the Precise panel
                scheduleSave();
              });
            }
          }
        };

        const onDown = (e: PointerEvent) => {
          downAt = [e.clientX, e.clientY];
          if (modeRef.current === 'sketch' && selectedId) {
            const mesh = itemMeshes.get(selectedId);
            if (!mesh) return;
            setRayFromEvent(e);
            const hit = ray.intersectObject(mesh, false)[0];
            if (!hit) return;
            clearAutocut(); // moving the piece invalidates a pending autocut plan
            const item = itemById(selectedId);
            if (glideRef.current) {
              // Glide drag: no drag plane — each move re-lays the piece flush
              // on whatever surface sits under the cursor.
              dragging = true;
              dragGlide = true;
              dragVertical = false;
              controls.enabled = false;
              return;
            }
            if (magnetRef.current && item) beginMagnet(item);
            if (vertDragRef.current) {
              // Vertical-drag mode: camera-facing vertical plane through the
              // piece — dragging up/down slides it along world up only.
              const n = camera.position.clone().sub(mesh.position);
              n.y = 0;
              if (n.lengthSq() < 1e-6) n.copy(camera.position).sub(controls.target).setY(0);
              if (n.lengthSq() < 1e-6) n.set(0, 0, 1);
              n.normalize();
              dragPlane.setFromNormalAndCoplanarPoint(n, mesh.position);
              if (ray.ray.intersectPlane(dragPlane, dragPoint)) {
                dragging = true;
                dragVertical = true;
                controls.enabled = false;
                dragOffsetY = mesh.position.y - dragPoint.y;
              }
              return;
            }
            // Drag the selected piece along a horizontal plane at its height.
            dragPlane.set(new THREE.Vector3(0, 1, 0), -mesh.position.y);
            if (ray.ray.intersectPlane(dragPlane, dragPoint)) {
              dragging = true;
              dragVertical = false;
              controls.enabled = false;
              dragOffset = { x: mesh.position.x - dragPoint.x, z: mesh.position.z - dragPoint.z };
            }
          }
        };

        const onMove = (e: PointerEvent) => {
          if (!dragging || !selectedId) return;
          const mesh = itemMeshes.get(selectedId);
          if (!mesh) return;
          setRayFromEvent(e);
          if (dragGlide) {
            // Wave 5: continuous "Lay" — follow the surface under the cursor.
            const item = itemById(selectedId);
            if (!item) return;
            const hit = visibleHits([...pickables, ...[...itemMeshes.values()].filter(m2 => m2 !== mesh)])[0];
            if (!hit || !hit.face) return;
            const n = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
            if (n.dot(ray.ray.direction) > 0) n.negate(); // the side we can see
            // Rotate the piece's most-upward local axis onto the surface
            // normal (keeps a flat board flat and an on-edge board on edge),
            // then rest it flush: lifted by half its size along that axis.
            const dims = isSheet(item)
              ? [item.lengthIn / 12, item.thickIn / 12, item.widthIn / 12]
              : [item.lengthIn / 12, lumberDims(item.size).depth / 12, lumberDims(item.size).thick / 12];
            let bestScore = -Infinity;
            let bestW = new THREE.Vector3(0, 1, 0);
            let bestDim = dims[1];
            [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 1)].forEach((a, i2) => {
              const w = a.applyQuaternion(mesh.quaternion);
              const t = w.dot(n);
              if (Math.abs(t) > bestScore) { bestScore = Math.abs(t); bestW = t >= 0 ? w : w.negate(); bestDim = dims[i2]; }
            });
            mesh.quaternion.premultiply(new THREE.Quaternion().setFromUnitVectors(bestW.normalize(), n));
            mesh.position.copy(hit.point).add(n.clone().multiplyScalar(bestDim / 2));
            setLivePos(mesh.position.toArray() as V3);
            return;
          }
          if (ray.ray.intersectPlane(dragPlane, dragPoint)) {
            if (dragVertical) {
              mesh.position.y = dragPoint.y + dragOffsetY;
            } else {
              mesh.position.x = dragPoint.x + dragOffset.x;
              mesh.position.z = dragPoint.z + dragOffset.z;
            }
            applyMagnet(mesh); // Wave 5: face-snap to nearby piece faces
            setLivePos(mesh.position.toArray() as V3);
          }
        };

        const onUp = (e: PointerEvent) => {
          if (dragging) {
            const moved = downAt ? Math.hypot(e.clientX - downAt[0], e.clientY - downAt[1]) : 99;
            endDrag(moved > 4);
            if (moved > 8) { downAt = null; return; } // was a drag, not a tap
          }
          if (!downAt) return;
          const moved = Math.hypot(e.clientX - downAt[0], e.clientY - downAt[1]);
          downAt = null;
          if (moved > 8) return; // it was an orbit drag, not a tap
          setRayFromEvent(e);
          tapAt();
        };

        // All tap handling for the current mode, driven off the shared `ray`.
        // Split out of onUp (wave 7) so taps can also be synthesized from a
        // world point — headlessly, a 1-1/2" chord top is a sub-pixel target.
        const tapAt = () => {
          const mode = modeRef.current;

          if (mode === 'layout') return; // layout works from its panel, not taps

          if (mode === 'generate') {
            // Wave 7: generators work off the MODEL only — tap a roof plane
            // (ladder: then its rake edge) and preview the framing.
            const hit = visibleHits(pickables)[0];
            if (!hit || !hit.face) return;
            const cfg = genCfgRef.current;
            if (cfg.kind === 'ladder' && genRegion) {
              // Second tap: the rake edge of the already-tapped plane
              const rg = genRegion;
              genRegion = null;
              if (cutMarker) { scene.remove(cutMarker); cutMarker = null; }
              setGenStage(0);
              const rel = hit.point.clone().sub(rg.O);
              showGenPreview(planLadder(rg, cfg, rel.dot(rg.U) * 12));
              return;
            }
            clearGenPreviewMeshes();
            setGenPlan(null);
            const region = detectPlaneRegion(hit);
            if (!region) {
              setGenPlan({ label: 'No workable plane found there — tap squarely on a roof surface.', ok: false });
              return;
            }
            if (cfg.kind === 'ladder') {
              genRegion = region;
              placeCutMarker(hit.point);
              setGenStage(1);
              return;
            }
            showGenPreview(cfg.kind === 'sheath' ? planSheath(region, cfg) : cfg.kind === 'purlin' ? planPurlins(region, cfg) : planFascia(region, cfg));
            return;
          }

          if (mode === 'layrun') {
            // Wave 5: two taps define a run; boards generate from the panel
            // config, resting flush on the tapped surface (averaged normals).
            const hit = visibleHits([...pickables, ...itemMeshes.values()])[0];
            if (!hit || !hit.face) return;
            const n = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
            if (n.dot(ray.ray.direction) > 0) n.negate();
            if (!runFirst) {
              runFirst = { p: hit.point.clone(), n };
              placeCutMarker(hit.point);
              setRunStage(1);
              return;
            }
            const p1 = runFirst.p.clone(), n1 = runFirst.n.clone(), p2 = hit.point.clone();
            runFirst = null;
            if (cutMarker) { scene.remove(cutMarker); cutMarker = null; }
            setRunStage(0);
            const spanIn = p1.distanceTo(p2) * 12;
            if (spanIn < 2) return;
            const cfg = runCfgRef.current;
            const d = lumberDims(cfg.size);
            const dir = p2.clone().sub(p1).normalize();
            // Surface "up": average of the two tap normals, squared up to the run
            let up = n1.add(n);
            if (up.lengthSq() < 0.25) up = n.clone();
            up.sub(dir.clone().multiplyScalar(up.dot(dir)));
            if (up.lengthSq() < 1e-4) up = new THREE.Vector3(0, 1, 0).sub(dir.clone().multiplyScalar(dir.y));
            if (up.lengthSq() < 1e-4) up = new THREE.Vector3(0, 0, 1);
            up.normalize();
            const lenIn = cfg.lenIn ?? Math.max(1, Math.round(spanIn * 16) / 16);
            const liftFt = (cfg.flat ? d.thick : d.depth) / 24;
            const acrossIn = cfg.flat ? d.depth : d.thick;
            const eulerFor = (axis: import('three').Vector3): V3 => {
              const m4 = cfg.flat
                ? new THREE.Matrix4().makeBasis(axis, up.clone().cross(axis).normalize(), up)
                : new THREE.Matrix4().makeBasis(axis, up, axis.clone().cross(up).normalize());
              const eu = new THREE.Euler().setFromQuaternion(new THREE.Quaternion().setFromRotationMatrix(m4));
              return [eu.x, eu.y, eu.z];
            };
            const boards: LumberItem[] = [];
            const pushBoard = (centre: import('three').Vector3, axis: import('three').Vector3) =>
              boards.push({ id: rid(), kind: 'lumber', size: cfg.size, lengthIn: lenIn, pos: centre.toArray() as V3, rot: eulerFor(axis) });
            if (cfg.mode === 'single') {
              // One board along the tapped line, starting at the first tap
              pushBoard(p1.clone().add(dir.clone().multiplyScalar(lenIn / 24)).add(up.clone().multiplyScalar(liftFt)), dir);
            } else {
              // Boards run ACROSS the line (decking/strapping), centred on it,
              // distributed from the first tap toward the second.
              const side = dir.clone().cross(up).normalize();
              const stations: number[] = [];
              if (cfg.mode === 'fill') {
                const pitch = acrossIn + cfg.gapIn;
                for (let t = acrossIn / 2; t + acrossIn / 2 <= spanIn + 1e-6 && stations.length < 200; t += pitch) stations.push(t);
              } else {
                for (let t = 0; t <= spanIn + 1e-6 && stations.length < 200; t += cfg.spacingIn) stations.push(t);
              }
              for (const t of stations) {
                pushBoard(p1.clone().add(dir.clone().multiplyScalar(t / 12)).add(up.clone().multiplyScalar(liftFt)), side);
              }
            }
            if (boards.length > 0) {
              mutate(() => {
                for (const b of boards) { sketchRef.current.members.push(b); buildItemMesh(b); }
                scheduleSave();
              });
            }
            setRunOn(false);
            return;
          }

          if (mode === 'measure') {
            const hits = visibleHits([...pickables, ...itemMeshes.values()]);
            if (!hits.length) return;
            const p = hits[0].point.clone();
            if (!measurePending) {
              measurePending = p;
              measureMarker = new THREE.Mesh(dotGeo, dotMat);
              measureMarker.renderOrder = 999;
              measureMarker.position.copy(p);
              scene.add(measureMarker);
            } else {
              const mm: SketchMeasurement = { id: rid(), a: measurePending.toArray() as V3, b: p.toArray() as V3 };
              mutate(() => {
                sketchRef.current.measurements.push(mm);
                buildMeasurement(mm);
                clearPendingMeasure();
                setMeasureCount(sketchRef.current.measurements.length);
                scheduleSave();
              });
            }
            return;
          }

          if (mode === 'cut' || mode === 'cutout') {
            const item = itemById(selectedId);
            const mesh = selectedId ? itemMeshes.get(selectedId) : undefined;
            if (!item || !mesh) return;
            const hit = ray.intersectObject(mesh, false)[0];
            if (!hit) return;
            const local = mesh.worldToLocal(hit.point.clone());
            const uIn = local.x * 12 + item.lengthIn / 2;
            const vIn = isSheet(item) ? item.widthIn / 2 - local.z * 12 : 0;
            if (mode === 'cut') {
              if (!isSheet(item)) {
                pendingCut = { itemId: item.id, type: 'lumber', uIn: Math.round(uIn * 16) / 16 };
                placeCutMarker(hit.point);
                updateCutPreview();
                setCutPending({ kind: 'lumber', uIn: pendingCut.uIn });
              } else if (!sheetCutFirst) {
                sheetCutFirst = { uIn, vIn };
                placeCutMarker(hit.point);
                setCutStage(1);
              } else {
                let u2 = uIn, v2 = vIn;
                const u1 = sheetCutFirst.uIn, v1 = sheetCutFirst.vIn;
                sheetCutFirst = null;
                // Snap near-axis lines to a true rip/crosscut
                const angRaw = (Math.atan2(v2 - v1, u2 - u1) * 180) / Math.PI;
                const near90 = Math.round(angRaw / 90) * 90;
                if (Math.abs(angRaw - near90) < 5) {
                  if (((near90 % 180) + 180) % 180 === 0) v2 = v1; else u2 = u1;
                }
                if (Math.hypot(u2 - u1, v2 - v1) < 1) { setCutStage(0); return; }
                pendingCut = { itemId: item.id, type: 'sheet', pts: [u1, v1, u2, v2] };
                const du = u2 - u1, dv = v2 - v1, len = Math.hypot(du, dv);
                const ext = item.lengthIn + item.widthIn;
                const q1 = shapeToWorld(item, mesh, u1 - (du / len) * ext, v1 - (dv / len) * ext, 0.03);
                const q2 = shapeToWorld(item, mesh, u1 + (du / len) * ext, v1 + (dv / len) * ext, 0.03);
                cutPreviewLine = new THREE.Line(
                  new THREE.BufferGeometry().setFromPoints([q1, q2]),
                  new THREE.LineBasicMaterial({ color: SEL, depthTest: false })
                );
                cutPreviewLine.renderOrder = 998;
                scene.add(cutPreviewLine);
                const angAbs = Math.abs(Math.round(angRaw));
                setCutPending({ kind: 'sheet', angleDeg: angAbs > 90 ? 180 - angAbs : angAbs });
                setCutStage(0);
              }
            } else if (isSheet(item)) {
              if (!cutoutFirst) {
                cutoutFirst = { uIn, vIn };
                placeCutMarker(hit.point);
                setCutoutStage(1);
              } else {
                const r: RectCut = {
                  x: Math.min(cutoutFirst.uIn, uIn), y: Math.min(cutoutFirst.vIn, vIn),
                  w: Math.abs(uIn - cutoutFirst.uIn), h: Math.abs(vIn - cutoutFirst.vIn),
                };
                cutoutFirst = null;
                if (cutMarker) { scene.remove(cutMarker); cutMarker = null; }
                setCutoutStage(0);
                const rect: RectCut = { x: Math.round(r.x * 16) / 16, y: Math.round(r.y * 16) / 16, w: Math.round(r.w * 16) / 16, h: Math.round(r.h * 16) / 16 };
                // On an angle-cut (polygon) sheet, only fully-interior cut-outs render
                const cornersOk = !item.poly || [[rect.x, rect.y], [rect.x + rect.w, rect.y], [rect.x + rect.w, rect.y + rect.h], [rect.x, rect.y + rect.h]]
                  .every(p => pointInPoly(p as Pt, item.poly!));
                if (rect.w >= 0.5 && rect.h >= 0.5 && cornersOk) {
                  mutate(() => {
                    item.cuts.push(rect);
                    rebuildItemGeometry(item);
                    setSelected({ ...item });
                    scheduleSave();
                  });
                }
              }
            }
            return;
          }

          if (mode === 'lay' || mode === 'line') {
            const item = itemById(selectedId);
            const mesh = selectedId ? itemMeshes.get(selectedId) : undefined;
            if (!item || !mesh) return;
            // Snap targets: the model plus other sketch pieces (sheathing over
            // sketched rafters is legitimate) — never the piece being placed.
            const targets = [...pickables, ...[...itemMeshes.values()].filter(m2 => m2 !== mesh)];
            const hit = visibleHits(targets)[0];
            if (!hit) return;

            if (mode === 'lay') {
              if (!hit.face) return;
              const n = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
              if (n.dot(ray.ray.direction) > 0) n.negate(); // use the side we can see
              const halfFt = (isSheet(item) ? item.thickIn : lumberDims(item.size).depth) / 24;
              mutate(() => {
                mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), n);
                mesh.position.copy(hit.point).add(n.multiplyScalar(halfFt));
                item.pos = mesh.position.toArray() as V3;
                item.rot = [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z];
                setSelected({ ...item });
                scheduleSave();
              });
              setTool('none');
              return;
            }

            // Run along line: two taps
            if (!snapFirst) {
              snapFirst = hit.point.clone();
              placeCutMarker(snapFirst);
              setSnapStage(1);
              return;
            }
            const p1 = snapFirst.clone(), p2 = hit.point.clone();
            snapFirst = null;
            if (cutMarker) { scene.remove(cutMarker); cutMarker = null; }
            setSnapStage(0);
            const spanFt = p1.distanceTo(p2);
            if (spanFt < 0.05) return;
            const dir = p2.clone().sub(p1).normalize();
            const worldUp = Math.abs(dir.y) > 0.95 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
            const zAxis = new THREE.Vector3().crossVectors(dir, worldUp).normalize();
            const yAxis = new THREE.Vector3().crossVectors(zAxis, dir).normalize();
            mutate(() => {
              mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(dir, yAxis, zAxis));
              if (autoLenRef.current && !(isSheet(item) && item.poly)) {
                item.lengthIn = Math.max(1, Math.round(spanFt * 12 * 16) / 16);
                rebuildItemGeometry(item);
                mesh.position.copy(p1).add(p2).multiplyScalar(0.5); // exactly the tapped span
              } else {
                mesh.position.copy(p1).add(dir.clone().multiplyScalar(item.lengthIn / 24)); // starts at the first tap
              }
              item.pos = mesh.position.toArray() as V3;
              item.rot = [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z];
              setSelected({ ...item });
              scheduleSave();
            });
            setTool('none');
            return;
          }

          if (mode === 'sketch') {
            const hits = visibleHits([...itemMeshes.values()]);
            selectItem(hits.length ? (hits[0].object.userData.sketchId as string) : null);
            return;
          }

          // Sketch off — original tap-to-identify behaviour
          const hits = visibleHits(pickables);
          setPicked(hits.length ? (hits[0].object.userData as { type: string; name: string }) : null);
        };

        renderer.domElement.addEventListener('pointerdown', onDown);
        renderer.domElement.addEventListener('pointermove', onMove);
        renderer.domElement.addEventListener('pointerup', onUp);
        // Verification hook: synthesize a tap straight down at a world point
        // (same code path as a pointer tap, minus the pixel plumbing).
        (window as unknown as Record<string, unknown>).__mmdlTapWorld = (wx: number, wy: number, wz: number) => {
          ray.set(new THREE.Vector3(wx, wy + 60, wz), new THREE.Vector3(0, -1, 0));
          tapAt();
        };
        const onCancel = () => { endDrag(false); downAt = null; };
        renderer.domElement.addEventListener('pointercancel', onCancel);

        // --- Render loop (+ measurement tag projection) ---
        const projV = new THREE.Vector3();
        const updateLabels = () => {
          if (measureLabels.size === 0) return;
          const w = mount.clientWidth, h = mount.clientHeight;
          for (const { el, mid } of measureLabels.values()) {
            projV.copy(mid).project(camera);
            if (projV.z > 1) { el.style.display = 'none'; continue; }
            el.style.display = '';
            el.style.left = `${((projV.x + 1) / 2) * w}px`;
            el.style.top = `${((-projV.y + 1) / 2) * h}px`;
          }
        };
        let raf = 0;
        const animate = () => { raf = requestAnimationFrame(animate); controls.update(); renderer.render(scene, camera); updateLabels(); };
        animate();
        const onResize = () => {
          camera.aspect = mount.clientWidth / mount.clientHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(mount.clientWidth, mount.clientHeight);
        };
        window.addEventListener('resize', onResize);

        // --- Sketch API exposed to the React UI ---
        const spawnPoint = (halfHeightFt: number) => {
          ray.setFromCamera(new THREE.Vector2(0, 0), camera);
          const hits = visibleHits([...pickables, ...itemMeshes.values()]);
          const p = hits.length
            ? hits[0].point.clone()
            : new THREE.Vector3(controls.target.x, box.min.y, controls.target.z);
          p.y += halfHeightFt;
          return p;
        };

        const addItem = (item: SketchItem) => mutate(() => {
          sketchRef.current.members.push(item);
          buildItemMesh(item);
          selectItem(item.id);
          scheduleSave();
        });

        const removeItem = (id: string, save: boolean) => {
          if (autocutPlan && autocutPlan.itemId === id) clearAutocut();
          destroyItemMesh(id);
          sketchRef.current.members = sketchRef.current.members.filter(m => m.id !== id);
          if (selectedId === id) { selectedId = null; setSelected(null); }
          if (save) scheduleSave();
        };

        const withSelected = (fn: (item: SketchItem, mesh: import('three').Mesh) => void) => {
          const item = itemById(selectedId);
          const mesh = selectedId ? itemMeshes.get(selectedId) : undefined;
          if (item && mesh) {
            clearAutocut(); // any edit invalidates a pending autocut preview
            fn(item, mesh);
          }
        };

        // World-space convex solid for a sketch piece (sheets: outline hull).
        const worldSolidOf = (item: SketchItem, mesh: import('three').Mesh): ConvexSolid => {
          let s: ConvexSolid;
          if (isSheet(item)) {
            s = sheetSolid(item.lengthIn, item.widthIn, item.thickIn, item.poly);
          } else {
            const d = lumberDims(item.size);
            s = lumberSolid(item.lengthIn / 12, d.depth / 12, d.thick / 12, item.e0, item.e1);
          }
          mesh.updateMatrixWorld(true);
          return { verts: s.verts.map(v => mesh.localToWorld(new THREE.Vector3(v[0], v[1], v[2])).toArray() as V3), faces: s.faces };
        };

        // --- Wave 5 helpers ---
        // Model-autocut probes only the VISIBLE layers: hide a layer (e.g.
        // walls) and it is left out of the collision pass.
        const probe = new THREE.Raycaster();
        const visibleModelTargets = () => pickables.filter(m2 => m2.visible && (m2.parent?.visible ?? true));

        // Punch a +1/8"-clearance rectangle (outward-rounded to the 1/16"
        // grid) into a sheet. True when a cut was added; false when skipped
        // (too small, already inside an opening, or outside an angled outline).
        const punchRect = (item: SheetItem, u0: number, v0: number, u1: number, v1: number): boolean => {
          const x = Math.floor((u0 - 0.125) * 16 + 1e-6) / 16;
          const y = Math.floor((v0 - 0.125) * 16 + 1e-6) / 16;
          const rect: RectCut = { x, y, w: Math.ceil((u1 + 0.125) * 16 - 1e-6) / 16 - x, h: Math.ceil((v1 + 0.125) * 16 - 1e-6) / 16 - y };
          if (rect.w < 0.25 || rect.h < 0.25) return false;
          const covered = item.cuts.some(ec =>
            rect.x >= ec.x - 1 / 32 && rect.y >= ec.y - 1 / 32 &&
            rect.x + rect.w <= ec.x + ec.w + 1 / 32 && rect.y + rect.h <= ec.y + ec.h + 1 / 32);
          if (covered) return false;
          const corners: Pt[] = [[rect.x, rect.y], [rect.x + rect.w, rect.y], [rect.x + rect.w, rect.y + rect.h], [rect.x, rect.y + rect.h]];
          if (item.poly && !corners.every(p => pointInPoly(p, item.poly!))) return false;
          item.cuts.push(rect);
          return true;
        };

        // --- Wave 7: Generate menu — tap-assisted roof-framing generators. ---
        // A tap recovers the roof PLANE REGION: the hit triangle's plane, all
        // visible model triangles coplanar with it (≤1.5° normal, ≤1/2"
        // offset), rasterized onto a 1" grid in plane space. Truss models
        // expose a roof plane only as chord-top stripes at 16"–24" o.c., so
        // the mask is closed morphologically (bridges framing gaps up to
        // ~3.5'), then the connected component under the tap becomes the
        // region. Plane frame: U across the slope (level), V up the slope,
        // N off the roof; (u,v) in inches from O.
        interface PlaneRegion {
          O: import('three').Vector3; U: import('three').Vector3; V: import('three').Vector3; N: import('three').Vector3;
          mask: Uint8Array; nu: number; nv: number;
          i0: number; i1: number; j0: number; j1: number; // true-cell bounds
          cells: number; // covered 1"-cell count (region area in sq in)
        }
        let genRegion: PlaneRegion | null = null;   // ladder: plane kept for the rake tap
        let genItems: SketchItem[] = [];            // pending preview pieces
        let genPreviewGroup: import('three').Group | null = null;
        const genMat = new THREE.MeshLambertMaterial({ color: 0x5C7F4F, transparent: true, opacity: 0.55, depthWrite: false });

        const worldFromUV = (rg: PlaneRegion, uIn: number, vIn: number) =>
          rg.O.clone().add(rg.U.clone().multiplyScalar(uIn / 12)).add(rg.V.clone().multiplyScalar(vIn / 12));
        const eulerFromBasis = (x: import('three').Vector3, y: import('three').Vector3, z: import('three').Vector3): V3 => {
          const e = new THREE.Euler().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z));
          return [e.x, e.y, e.z];
        };
        const round16 = (x: number) => Math.round(x * 16) / 16;

        const detectPlaneRegion = (hit: import('three').Intersection): PlaneRegion | null => {
          if (!hit.face) return null;
          const N = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
          if (N.dot(ray.ray.direction) > 0) N.negate(); // the side we can see
          const down = new THREE.Vector3(0, -1, 0);
          let fall = down.clone().sub(N.clone().multiplyScalar(down.dot(N)));
          if (fall.lengthSq() < 1e-4) fall = new THREE.Vector3(1, 0, 0).sub(N.clone().multiplyScalar(N.x)); // dead-flat plane
          fall.normalize();
          const Vv = fall.clone().negate();
          const Uv = fall.clone().cross(N).normalize();
          if (new THREE.Vector3().crossVectors(Uv, Vv).dot(N) < 0) Uv.negate(); // right-handed (U,V,N)
          const P0 = hit.point.clone();
          const d0 = N.dot(P0);
          const COS = Math.cos((1.5 * Math.PI) / 180);
          const OFF = 0.045; // ft ≈ 1/2"
          const CAP = 1560;  // inches each way from the tap (130')
          const va = new THREE.Vector3(), vb = new THREE.Vector3(), vc = new THREE.Vector3();
          const ab = new THREE.Vector3(), ac = new THREE.Vector3(), tn = new THREE.Vector3();
          const triUV: number[] = []; // u1,v1,u2,v2,u3,v3 per kept triangle
          let bu0 = Infinity, bv0 = Infinity, bu1 = -Infinity, bv1 = -Infinity;
          for (const m2 of visibleModelTargets()) {
            const g2 = m2.geometry as import('three').BufferGeometry;
            const attr = g2.getAttribute('position');
            const idx = g2.getIndex();
            const triCount = (idx ? idx.count : attr.count) / 3;
            m2.updateMatrixWorld(true);
            for (let t2 = 0; t2 < triCount; t2++) {
              va.fromBufferAttribute(attr, idx ? idx.getX(t2 * 3) : t2 * 3).applyMatrix4(m2.matrixWorld);
              vb.fromBufferAttribute(attr, idx ? idx.getX(t2 * 3 + 1) : t2 * 3 + 1).applyMatrix4(m2.matrixWorld);
              vc.fromBufferAttribute(attr, idx ? idx.getX(t2 * 3 + 2) : t2 * 3 + 2).applyMatrix4(m2.matrixWorld);
              if (Math.abs(N.dot(va) - d0) > OFF || Math.abs(N.dot(vb) - d0) > OFF || Math.abs(N.dot(vc) - d0) > OFF) continue;
              tn.crossVectors(ab.subVectors(vb, va), ac.subVectors(vc, va));
              if (tn.lengthSq() < 1e-12) continue;
              tn.normalize();
              // Orientation-agnostic: truss models carry unreliable winding
              // (chord tops here face "inward" — the viewer renders DoubleSide
              // for the same reason), so parallel counts, not just same-facing.
              if (Math.abs(tn.dot(N)) < COS) continue;
              let ok = true;
              const six: number[] = [];
              for (const p of [va, vb, vc]) {
                const u = (p.x - P0.x) * Uv.x + (p.y - P0.y) * Uv.y + (p.z - P0.z) * Uv.z;
                const v = (p.x - P0.x) * Vv.x + (p.y - P0.y) * Vv.y + (p.z - P0.z) * Vv.z;
                const uI = u * 12, vI = v * 12;
                if (Math.abs(uI) > CAP || Math.abs(vI) > CAP) { ok = false; break; }
                six.push(uI, vI);
              }
              if (!ok) continue;
              for (let k = 0; k < 6; k += 2) {
                if (six[k] < bu0) bu0 = six[k];
                if (six[k] > bu1) bu1 = six[k];
                if (six[k + 1] < bv0) bv0 = six[k + 1];
                if (six[k + 1] > bv1) bv1 = six[k + 1];
              }
              triUV.push(...six);
            }
          }
          if (triUV.length === 0) return null;
          const PAD = 24;
          const minU = Math.floor(bu0) - PAD, minV = Math.floor(bv0) - PAD;
          const nu = Math.min(3200, Math.ceil(bu1) - minU + PAD + 1);
          const nv = Math.min(3200, Math.ceil(bv1) - minV + PAD + 1);
          const mask = new Uint8Array(nu * nv);
          for (let t2 = 0; t2 < triUV.length; t2 += 6) {
            const x1 = triUV[t2] - minU, y1 = triUV[t2 + 1] - minV;
            const x2 = triUV[t2 + 2] - minU, y2 = triUV[t2 + 3] - minV;
            const x3 = triUV[t2 + 4] - minU, y3 = triUV[t2 + 5] - minV;
            const i0 = Math.max(0, Math.floor(Math.min(x1, x2, x3)));
            const i1 = Math.min(nu - 1, Math.floor(Math.max(x1, x2, x3)) + 1);
            const j0 = Math.max(0, Math.floor(Math.min(y1, y2, y3)));
            const j1 = Math.min(nv - 1, Math.floor(Math.max(y1, y2, y3)) + 1);
            for (let j = j0; j <= j1; j++) {
              const py = j + 0.5;
              for (let i = i0; i <= i1; i++) {
                const px = i + 0.5;
                const s1 = (px - x2) * (y1 - y2) - (x1 - x2) * (py - y2);
                const s2 = (px - x3) * (y2 - y3) - (x2 - x3) * (py - y3);
                const s3 = (px - x1) * (y3 - y1) - (x3 - x1) * (py - y1);
                const neg = s1 < 0 || s2 < 0 || s3 < 0;
                const pos2 = s1 > 0 || s2 > 0 || s3 > 0;
                if (!(neg && pos2)) mask[j * nu + i] = 1;
              }
            }
            // stamp each vertex's cell — keeps sub-1" slivers from vanishing
            for (const [vx, vy] of [[x1, y1], [x2, y2], [x3, y3]] as const) {
              const i = Math.floor(vx), j = Math.floor(vy);
              if (i >= 0 && j >= 0 && i < nu && j < nv) mask[j * nu + i] = 1;
            }
          }
          // The tap point itself is on the plane — make sure its cell is set
          // even when the hit triangle is a sliver that missed the raster.
          mask[(0 - minV) * nu + (0 - minU)] = 1;
          // Close chord-top stripes into one region, keep the tapped component
          const closed = closeMask(mask, nu, nv, 21);
          const region = componentAt(closed, nu, nv, (0 - minV) * nu + (0 - minU));
          if (!region) return null;
          let count = 0, i0 = nu, i1 = -1, j0 = nv, j1 = -1;
          for (let j = 0; j < nv; j++) {
            for (let i = 0; i < nu; i++) {
              if (!region[j * nu + i]) continue;
              count++;
              if (i < i0) i0 = i;
              if (i > i1) i1 = i;
              if (j < j0) j0 = j;
              if (j > j1) j1 = j;
            }
          }
          if (count < 400) return null; // under ~3 sq ft — not a workable plane
          const O = P0.clone().add(Uv.clone().multiplyScalar(minU / 12)).add(Vv.clone().multiplyScalar(minV / 12));
          return { O, U: Uv, V: Vv, N, mask: region, nu, nv, i0, i1, j0, j1, cells: count };
        };

        // Merged covered u-intervals (inches) of one mask row; gaps ≤2" join.
        const rowSpans = (rg: PlaneRegion, j: number): [number, number][] => {
          const spans: [number, number][] = [];
          let start = -1;
          for (let i = rg.i0; i <= rg.i1 + 1; i++) {
            const on = i <= rg.i1 && rg.mask[j * rg.nu + i] === 1;
            if (on && start === -1) start = i;
            else if (!on && start !== -1) { spans.push([start, i]); start = -1; }
          }
          const merged: [number, number][] = [];
          for (const s of spans) {
            const last = merged[merged.length - 1];
            if (last && s[0] - last[1] <= 2) last[1] = s[1];
            else merged.push([s[0], s[1]]);
          }
          return merged;
        };

        // Split [a, b] into ≤16' pieces; cb gets (centre, length) per piece.
        const splitStock = (a: number, b: number, cb: (centre: number, len: number) => void) => {
          let u = a;
          while (b - u > 0.5) {
            const len = Math.min(b - u, STOCK_MAX_IN);
            cb(u + len / 2, len);
            u += len;
          }
        };

        const planSheath = (rg: PlaneRegion, cfg: GenCfg): { items: SketchItem[]; label: string } => {
          const L = 96, W = 48, gap = cfg.plyGapIn, tIn = cfg.plyThickIn;
          const rot = eulerFromBasis(rg.U, rg.N, rg.V.clone().negate());
          const liftV = rg.N.clone().multiplyScalar(tIn / 24);
          const items: SketchItem[] = [];
          let full = 0, cutN = 0, truncated = false;
          for (let row = 0, vb = rg.j0; vb <= rg.j1 && !truncated; vb += W + gap, row++) {
            const uStart = cfg.plyStagger && row % 2 === 1 ? rg.i0 - L / 2 : rg.i0;
            for (let ub = uStart; ub <= rg.i1 && !truncated; ub += L + gap) {
              if (items.length >= 400) { truncated = true; break; }
              // centre-cells of the candidate rect (unclamped, then clamped)
              const ciA0 = Math.ceil(ub - 0.5 + 1e-6), ciB0 = Math.floor(ub + L - 0.5 - 1e-6);
              const cjA0 = Math.ceil(vb - 0.5 + 1e-6), cjB0 = Math.floor(vb + W - 0.5 - 1e-6);
              const ciA = Math.max(0, ciA0), ciB = Math.min(rg.nu - 1, ciB0);
              const cjA = Math.max(0, cjA0), cjB = Math.min(rg.nv - 1, cjB0);
              if (ciB < ciA || cjB < cjA) continue;
              let covered = 0;
              for (let j = cjA; j <= cjB; j++) {
                for (let i = ciA; i <= ciB; i++) if (rg.mask[j * rg.nu + i]) covered++;
              }
              if (covered === 0) continue;
              const total = (ciB - ciA + 1) * (cjB - cjA + 1);
              if (covered === total && ciA === ciA0 && ciB === ciB0 && cjA === cjA0 && cjB === cjB0) {
                items.push({
                  id: rid(), kind: 'sheet', thickIn: tIn, widthIn: W, lengthIn: L, cuts: [],
                  pos: worldFromUV(rg, ub + L / 2, vb + W / 2).add(liftV).toArray() as V3, rot,
                });
                full++;
                continue;
              }
              // Edge sheet: cells FULLY inside the rect, clipped to the region
              const fiA = Math.max(0, Math.ceil(ub - 1e-6)), fiB = Math.min(rg.nu - 1, Math.floor(ub + L + 1e-6) - 1);
              const fjA = Math.max(0, Math.ceil(vb - 1e-6)), fjB = Math.min(rg.nv - 1, Math.floor(vb + W + 1e-6) - 1);
              if (fiB < fiA || fjB < fjA) continue;
              const su = fiB - fiA + 1, sv = fjB - fjA + 1;
              const sub = new Uint8Array(su * sv);
              let any = false;
              for (let j = 0; j < sv; j++) {
                for (let i = 0; i < su; i++) {
                  if (rg.mask[(fjA + j) * rg.nu + (fiA + i)]) { sub[j * su + i] = 1; any = true; }
                }
              }
              if (!any) continue;
              for (const o of maskOutlines(sub, su, sv)) {
                const abs: Pt[] = o.outer.map(p => [fiA + p[0], fjA + p[1]]);
                const bb = polyBounds(abs);
                if (bb.w < 3 || bb.h < 3) continue; // sliver
                const shifted: Pt[] = abs.map(p => [round16(p[0] - bb.x), round16(p[1] - bb.y)]);
                items.push({
                  id: rid(), kind: 'sheet', thickIn: tIn,
                  widthIn: round16(bb.h), lengthIn: round16(bb.w),
                  poly: polyIsRect(shifted, bb.w, bb.h) ? undefined : shifted, cuts: [],
                  pos: worldFromUV(rg, bb.x + bb.w / 2, bb.y + bb.h / 2).add(liftV).toArray() as V3, rot,
                });
                cutN++;
              }
            }
          }
          const tl = PLY_THICKNESSES.find(p => Math.abs(p.thick - tIn) < 1e-6)?.label ?? formatInches(tIn);
          // Plane pitch as rise-per-12 — lets the user spot a mis-tap (e.g.
          // the flat ceiling plane) before generating anything.
          const rise = Math.tan(Math.acos(Math.min(1, Math.abs(rg.N.y)))) * 12;
          const pitchLbl = rise > 48 ? 'vertical' : rise < 0.25 ? 'flat' : `${Math.round(rise * 2) / 2}/12`;
          return { items, label: `${items.length} sheets (${full} full, ${cutN} cut) · ${tl} ply · plane ≈${Math.round(rg.cells / 144)} sq ft @ ${pitchLbl}${truncated ? ' — capped at 400' : ''}` };
        };

        const planPurlins = (rg: PlaneRegion, cfg: GenCfg): { items: SketchItem[]; label: string } => {
          const d = lumberDims(cfg.purlinSize);
          const across = cfg.purlinFlat ? d.depth : d.thick;
          const liftV = rg.N.clone().multiplyScalar((cfg.purlinFlat ? d.thick : d.depth) / 24);
          const rot = cfg.purlinFlat
            ? eulerFromBasis(rg.U, rg.V, rg.N)
            : eulerFromBasis(rg.U, rg.N, rg.V.clone().negate());
          const items: SketchItem[] = [];
          let lines = 0;
          for (let v = rg.j0 + across / 2; v <= rg.j1 + 1 - across / 2 + 0.001 && lines < 120; v += cfg.purlinSpacingIn) {
            const j = Math.min(rg.nv - 1, Math.max(0, Math.round(v - 0.5)));
            let used = false;
            for (const [a, b] of rowSpans(rg, j)) {
              if (b - a < 12) continue;
              splitStock(a, b, (centre, len) => {
                items.push({
                  id: rid(), kind: 'lumber', size: cfg.purlinSize, lengthIn: round16(len),
                  pos: worldFromUV(rg, centre, v).add(liftV).toArray() as V3, rot,
                });
              });
              used = true;
            }
            if (used) lines++;
          }
          return { items, label: `${lines} purlin lines, ${items.length} boards (butt-spliced at 16') · ${cfg.purlinSize} ${cfg.purlinFlat ? 'on flat' : 'on edge'} @ ${formatInches(cfg.purlinSpacingIn)} o.c.` };
        };

        const planFascia = (rg: PlaneRegion, cfg: GenCfg): { items: SketchItem[]; label: string } => {
          const d = lumberDims(cfg.fasciaSize);
          // Lowest covered cell per column → the eave line and its runs
          const bottom = new Int32Array(rg.nu).fill(-1);
          let vEave = rg.nv;
          for (let i = rg.i0; i <= rg.i1; i++) {
            for (let j = rg.j0; j <= rg.j1; j++) {
              if (rg.mask[j * rg.nu + i]) { bottom[i] = j; if (j < vEave) vEave = j; break; }
            }
          }
          if (vEave >= rg.nv) return { items: [], label: 'No eave edge found on that plane.' };
          const runs: [number, number][] = [];
          let start = -1;
          for (let i = rg.i0; i <= rg.i1 + 1; i++) {
            const on = i <= rg.i1 && bottom[i] !== -1 && bottom[i] <= vEave + 1;
            if (on && start === -1) start = i;
            else if (!on && start !== -1) { runs.push([start, i]); start = -1; }
          }
          const merged: [number, number][] = [];
          for (const r2 of runs) {
            const last = merged[merged.length - 1];
            if (last && r2[0] - last[1] <= 4) last[1] = r2[1];
            else merged.push([r2[0], r2[1]]);
          }
          // Plumb board on edge along the eave, outer face at the eave line
          const up = new THREE.Vector3(0, 1, 0);
          const fall = rg.V.clone().negate();
          const hOut = fall.clone().sub(up.clone().multiplyScalar(fall.dot(up)));
          if (hOut.lengthSq() < 1e-6) hOut.copy(rg.N).setY(0);
          hOut.normalize();
          const X = rg.U.clone();
          const Z = new THREE.Vector3().crossVectors(X, up);
          if (Z.dot(hOut) < 0) { X.negate(); Z.negate(); }
          Z.normalize();
          const rot = eulerFromBasis(X, up, Z);
          const items: SketchItem[] = [];
          for (const [a, b] of merged) {
            if (b - a < 12) continue;
            splitStock(a, b, (centre, len) => {
              const pos = worldFromUV(rg, centre, vEave)
                .add(hOut.clone().multiplyScalar(-d.thick / 24))
                .add(up.clone().multiplyScalar(-d.depth / 24));
              items.push({ id: rid(), kind: 'lumber', size: cfg.fasciaSize, lengthIn: round16(len), pos: pos.toArray() as V3, rot });
            });
          }
          return { items, label: `${items.length} sub-fascia board${items.length === 1 ? '' : 's'} along the eave (${merged.length} run${merged.length === 1 ? '' : 's'}, spliced at 16') · ${cfg.fasciaSize} on edge, plumb` };
        };

        const planLadder = (rg: PlaneRegion, cfg: GenCfg, uTap: number): { items: SketchItem[]; label: string } => {
          const d = lumberDims(cfg.ladderSize);
          // Per-row extreme columns; the rake is the straight edge nearest the tap
          const minI: number[] = [], maxI: number[] = [], rows: number[] = [];
          for (let j = rg.j0; j <= rg.j1; j++) {
            let lo = -1, hi = -1;
            for (let i = rg.i0; i <= rg.i1; i++) {
              if (!rg.mask[j * rg.nu + i]) continue;
              if (lo === -1) lo = i;
              hi = i;
            }
            if (lo !== -1) { minI.push(lo); maxI.push(hi); rows.push(j); }
          }
          if (rows.length < 4) return { items: [], label: 'That plane is too small for a ladder.' };
          const median = (xs: number[]) => [...xs].sort((p, q) => p - q)[Math.floor(xs.length / 2)];
          const mL = median(minI), mR = median(maxI) + 1;
          const s = Math.abs(uTap - mL) < Math.abs(uTap - mR) ? -1 : 1;
          const uEdge = s === -1 ? mL : mR;
          // Rows whose edge actually sits on the rake line (±2")
          let jLo = Infinity, jHi = -Infinity;
          for (let k = 0; k < rows.length; k++) {
            const e = s === -1 ? minI[k] : maxI[k] + 1;
            if (Math.abs(e - uEdge) <= 2) { if (rows[k] < jLo) jLo = rows[k]; if (rows[k] > jHi) jHi = rows[k]; }
          }
          if (jHi - jLo < 12) return { items: [], label: 'Could not follow a straight rake edge there.' };
          const vLo = jLo, vHi = jHi + 1;
          const items: SketchItem[] = [];
          // Lookouts: flat on the plane, crossing the rake out to the fly rafter
          const lkRot = eulerFromBasis(rg.U, rg.V, rg.N);
          const lkLift = rg.N.clone().multiplyScalar(d.thick / 24);
          const lkLen = cfg.ladderInboardIn + cfg.ladderOverhangIn;
          const lkU = uEdge + s * (cfg.ladderOverhangIn - cfg.ladderInboardIn) / 2;
          let lookouts = 0;
          for (let v = vLo + cfg.ladderStartIn + d.depth / 2; v + d.depth / 2 <= vHi + 0.001 && lookouts < 120; v += cfg.ladderSpacingIn) {
            items.push({
              id: rid(), kind: 'lumber', size: cfg.ladderSize, lengthIn: round16(lkLen),
              pos: worldFromUV(rg, lkU, v).add(lkLift).toArray() as V3, rot: lkRot,
            });
            lookouts++;
          }
          // Fly rafter: on edge along the slope, capping the outboard ends;
          // top edge flush with the lookout tops
          const flyRot = eulerFromBasis(rg.V, rg.N, rg.U);
          const flyU = uEdge + s * (cfg.ladderOverhangIn + d.thick / 2);
          const flyLift = rg.N.clone().multiplyScalar((d.thick - d.depth / 2) / 12);
          let flyPieces = 0;
          splitStock(vLo, vHi, (centre, len) => {
            items.push({
              id: rid(), kind: 'lumber', size: cfg.ladderSize, lengthIn: round16(len),
              pos: worldFromUV(rg, flyU, centre).add(flyLift).toArray() as V3, rot: flyRot,
            });
            flyPieces++;
          });
          return { items, label: `${lookouts} lookouts (${cfg.ladderSize} flat @ ${formatInches(cfg.ladderSpacingIn)} o.c.) + fly rafter (${flyPieces} pc${flyPieces === 1 ? '' : 's'} on edge) · ${formatInches(cfg.ladderOverhangIn)} overhang` };
        };

        const clearGenPreviewMeshes = () => {
          if (genPreviewGroup) {
            for (const c of genPreviewGroup.children) (c as import('three').Mesh).geometry.dispose();
            scene.remove(genPreviewGroup);
            genPreviewGroup = null;
          }
          genItems = [];
        };
        const showGenPreview = (plan: { items: SketchItem[]; label: string }) => {
          clearGenPreviewMeshes();
          if (plan.items.length === 0) { setGenPlan({ label: plan.label, ok: false }); return; }
          genItems = plan.items;
          const g = new THREE.Group();
          for (const it of plan.items) {
            const m2 = new THREE.Mesh(buildItemGeometry(it), genMat);
            m2.position.fromArray(it.pos);
            m2.rotation.set(it.rot[0], it.rot[1], it.rot[2]);
            g.add(m2);
          }
          scene.add(g);
          genPreviewGroup = g;
          setGenPlan({ label: plan.label, ok: true });
        };
        const genCancelScene = () => {
          clearGenPreviewMeshes();
          if (genRegion) {
            genRegion = null;
            if (cutMarker) { scene.remove(cutMarker); cutMarker = null; }
          }
          setGenPlan(null);
          setGenStage(0);
        };

        // Layout tool: translucent highlight on the chosen face of the base.
        let layoutFaceMesh: import('three').Mesh | null = null;

        const sketch: SketchApi = {
          addLumber: (size, lengthIn) => {
            const d = lumberDims(size);
            const p = spawnPoint(d.depth / 24);
            addItem({ id: rid(), kind: 'lumber', size, lengthIn, pos: p.toArray() as V3, rot: [0, 0, 0] });
          },
          addSheet: (thickIn, widthIn, lengthIn) => {
            const p = spawnPoint(thickIn / 24);
            addItem({ id: rid(), kind: 'sheet', thickIn, widthIn, lengthIn, cuts: [], pos: p.toArray() as V3, rot: [0, 0, 0] });
          },
          select: selectItem,
          nudge: (axis, dir, stepIn) => withSelected((item, mesh) => mutate(() => {
            mesh.position[axis] += (dir * stepIn) / 12;
            item.pos = mesh.position.toArray() as V3;
            setSelected({ ...item });
            scheduleSave();
          })),
          rotate: (axis, deg, snap) => withSelected((item, mesh) => mutate(() => {
            // Local-space rotation: after a "Lay on surface" snap, Y 90° rolls
            // about the surface normal; X rolls about the piece's own length.
            const rad = (deg * Math.PI) / 180;
            if (axis === 'x') mesh.rotateX(rad); else if (axis === 'y') mesh.rotateY(rad); else mesh.rotateZ(rad);
            if (snap) {
              // Snap-15 mode: land each stored angle on a clean 15° multiple.
              const s = Math.PI / 12;
              mesh.rotation.set(
                Math.round(mesh.rotation.x / s) * s,
                Math.round(mesh.rotation.y / s) * s,
                Math.round(mesh.rotation.z / s) * s
              );
            }
            item.rot = [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z];
            setSelected({ ...item });
            scheduleSave();
          })),
          // Wave 6: screen-intuitive ground moves — camera-right and
          // camera-forward projected onto the ground plane.
          nudgeCam: (rightSteps, fwdSteps, stepIn) => withSelected((item, mesh) => mutate(() => {
            const fwd = camera.getWorldDirection(new THREE.Vector3());
            fwd.y = 0;
            if (fwd.lengthSq() < 1e-4) {
              // Top view: the look direction is straight down — use screen-up.
              fwd.set(0, 1, 0).applyQuaternion(camera.quaternion);
              fwd.y = 0;
            }
            if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1);
            fwd.normalize();
            const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize();
            mesh.position
              .add(right.multiplyScalar((rightSteps * stepIn) / 12))
              .add(fwd.multiplyScalar((fwdSteps * stepIn) / 12));
            item.pos = mesh.position.toArray() as V3;
            setSelected({ ...item });
            scheduleSave();
          })),
          // Wave 6: yaw about the world up axis (spin the piece flat).
          yawWorld: (deg, snap) => withSelected((item, mesh) => mutate(() => {
            mesh.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), (deg * Math.PI) / 180);
            if (snap) {
              const s = Math.PI / 12;
              mesh.rotation.set(
                Math.round(mesh.rotation.x / s) * s,
                Math.round(mesh.rotation.y / s) * s,
                Math.round(mesh.rotation.z / s) * s
              );
            }
            item.rot = [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z];
            setSelected({ ...item });
            scheduleSave();
          })),
          // Wave 6: swap the whole sketch in (undo/redo). Rebuilds every mesh
          // and measurement, keeps the selection when the piece still exists,
          // and autosaves the restored state.
          restoreSketch: data => {
            clearPendingCut();
            clearAutocut();
            clearPendingMeasure();
            const keepId = selectedId;
            selectedId = null;
            for (const id of [...itemMeshes.keys()]) destroyItemMesh(id);
            for (const { el } of measureLabels.values()) el.remove();
            measureLabels.clear();
            measureGroup.clear();
            sketchRef.current = JSON.parse(JSON.stringify(data)) as SketchData;
            for (const m of sketchRef.current.members) buildItemMesh(m);
            for (const mm of sketchRef.current.measurements) buildMeasurement(mm);
            setMeasureCount(sketchRef.current.measurements.length);
            selectItem(keepId && sketchRef.current.members.some(m => m.id === keepId) ? keepId : null);
            scheduleSave();
          },
          // Wave 7: apply / dismiss a pending Generate preview. Applying is
          // ONE mutate() — the whole generation undoes in a single Ctrl+Z.
          genConfirm: () => {
            if (genItems.length === 0) { genCancelScene(); return; }
            const items = genItems;
            genItems = []; // keep clearGenPreviewMeshes from touching them
            genCancelScene();
            mutate(() => {
              for (const it of items) {
                sketchRef.current.members.push(it);
                buildItemMesh(it);
              }
              scheduleSave();
            });
          },
          genCancel: genCancelScene,
          setPosition: (axis, valueFt) => withSelected((item, mesh) => mutate(() => {
            mesh.position[axis] = valueFt;
            item.pos = mesh.position.toArray() as V3;
            setSelected({ ...item });
            scheduleSave();
          })),
          setRotationDeg: (axis, deg) => withSelected((item, mesh) => mutate(() => {
            mesh.rotation[axis] = (deg * Math.PI) / 180;
            item.rot = [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z];
            setSelected({ ...item });
            scheduleSave();
          })),
          setLength: lengthIn => withSelected(item => {
            if (isSheet(item) && item.poly) return; // angle-cut sheets resize by cutting
            if (Math.abs(item.lengthIn - lengthIn) < 1e-9) return;
            mutate(() => {
              item.lengthIn = lengthIn;
              rebuildItemGeometry(item);
              setSelected({ ...item });
              scheduleSave();
            });
          }),
          setWidth: widthIn => withSelected(item => {
            if (!isSheet(item)) return;
            if (Math.abs(item.widthIn - widthIn) < 1e-9) return;
            mutate(() => {
              item.widthIn = widthIn;
              rebuildItemGeometry(item);
              setSelected({ ...item });
              scheduleSave();
            });
          }),
          setThick: thickIn => withSelected(item => {
            if (!isSheet(item)) return;
            if (Math.abs(item.thickIn - thickIn) < 1e-9) return;
            mutate(() => {
              item.thickIn = thickIn;
              rebuildItemGeometry(item);
              setSelected({ ...item });
              scheduleSave();
            });
          }),
          duplicate: () => withSelected(item => {
            const copy: SketchItem = isSheet(item)
              ? { ...item, id: rid(), cuts: item.cuts.map(c => ({ ...c })), poly: item.poly?.map(p => [...p] as Pt), pos: [item.pos[0] + 2, item.pos[1], item.pos[2]], rot: [...item.rot] as V3 }
              : { ...item, id: rid(), pos: [item.pos[0] + 2, item.pos[1], item.pos[2]], rot: [...item.rot] as V3 };
            addItem(copy);
          }),
          remove: () => {
            const id = selectedId;
            if (id) mutate(() => removeItem(id, true));
          },
          clearMeasurements: () => {
            if (sketchRef.current.measurements.length === 0) return;
            mutate(() => {
              for (const mm of [...sketchRef.current.measurements]) removeMeasurement(mm.id);
            });
          },
          cancelMeasure: clearPendingMeasure,
          cancelCut: clearPendingCut,
          undoCutout: () => withSelected(item => {
            if (!isSheet(item) || item.cuts.length === 0) return;
            mutate(() => {
              item.cuts.pop();
              rebuildItemGeometry(item);
              setSelected({ ...item });
              scheduleSave();
            });
          }),
          // --- Wave 5: layout tool (studs / lookouts / rafters off a base) ---
          setLayoutFace: face => {
            if (layoutFaceMesh) {
              layoutFaceMesh.parent?.remove(layoutFaceMesh);
              layoutFaceMesh.geometry.dispose();
              (layoutFaceMesh.material as import('three').Material).dispose();
              layoutFaceMesh = null;
            }
            if (face === null) return;
            const item = itemById(selectedId);
            const mesh = selectedId ? itemMeshes.get(selectedId) : undefined;
            if (!item || !mesh || isSheet(item)) return;
            const bd = lumberDims(item.size);
            const quad = new THREE.Mesh(
              new THREE.PlaneGeometry(item.lengthIn / 12, (face < 2 ? bd.depth : bd.thick) / 12),
              new THREE.MeshBasicMaterial({ color: SEL, transparent: true, opacity: 0.45, side: THREE.DoubleSide, depthTest: false })
            );
            quad.renderOrder = 996;
            const lift = 0.006;
            if (face === 0) quad.position.z = bd.thick / 24 + lift;
            else if (face === 1) quad.position.z = -(bd.thick / 24 + lift);
            else if (face === 2) { quad.rotation.x = -Math.PI / 2; quad.position.y = bd.depth / 24 + lift; }
            else { quad.rotation.x = Math.PI / 2; quad.position.y = -(bd.depth / 24 + lift); }
            mesh.add(quad);
            layoutFaceMesh = quad;
          },
          layoutGenerate: cfg => withSelected((item, mesh) => {
            if (isSheet(item)) return;
            if (!(cfg.spacingIn >= 1) || !(cfg.lengthIn >= 1) || cfg.startIn < 0) return;
            const bd = lumberDims(item.size);
            // Faces in the base's local frame: 0/1 = wide (±Z), 2/3 = edge (±Y)
            const faceN = ([[0, 0, 1], [0, 0, -1], [0, 1, 0], [0, -1, 0]] as const)[cfg.face] ?? [0, 0, 1];
            const off = cfg.face < 2 ? bd.thick / 24 : bd.depth / 24;
            const nl = new THREE.Vector3(faceN[0], faceN[1], faceN[2]);
            // Child axis: the face normal tilted to the pitch about the base's
            // length axis (0/12 = square off the face). Children butt
            // end-grain to the face, centres at the o.c. spacing.
            const dl = nl.clone().applyAxisAngle(new THREE.Vector3(1, 0, 0), Math.atan2(cfg.pitchN, 12));
            const yl = new THREE.Vector3(1, 0, 0).cross(dl);
            const q = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(dl, yl, new THREE.Vector3(1, 0, 0)));
            mesh.updateMatrixWorld(true);
            const eul = new THREE.Euler().setFromQuaternion(mesh.quaternion.clone().multiply(q));
            const auto = Math.floor((item.lengthIn - cfg.startIn) / cfg.spacingIn + 1e-6) + 1;
            const count = Math.max(0, Math.min(cfg.count ?? auto, 200));
            const children: LumberItem[] = [];
            for (let k = 0; k < count; k++) {
              const s = cfg.startIn + k * cfg.spacingIn;
              if (s > item.lengthIn + 1e-6) break;
              const centre = mesh.localToWorld(
                new THREE.Vector3(s / 12 - item.lengthIn / 24, 0, 0)
                  .add(nl.clone().multiplyScalar(off))
                  .add(dl.clone().multiplyScalar(cfg.lengthIn / 24))
              );
              children.push({ id: rid(), kind: 'lumber', size: cfg.size, lengthIn: cfg.lengthIn, pos: centre.toArray() as V3, rot: [eul.x, eul.y, eul.z] });
            }
            if (children.length === 0) return;
            mutate(() => {
              for (const p of children) { sketchRef.current.members.push(p); buildItemMesh(p); }
              scheduleSave();
            });
          }),
          // --- Wave 4: Autocut collisions (sketch pieces only, not the model) ---
          autocutRun: () => withSelected((item, mesh) => {
            setTool('none');
            const mySolid = worldSolidOf(item, mesh);
            const colliders: ConvexSolid[] = [];
            for (const other of sketchRef.current.members) {
              if (other.id === item.id) continue;
              const om = itemMeshes.get(other.id);
              if (!om) continue;
              const os = worldSolidOf(other, om);
              if (solidsCollide(mySolid, os)) colliders.push(os);
            }
            const modelTargets = visibleModelTargets();
            mesh.updateMatrixWorld(true);

            if (!isSheet(item)) {
              // Lumber: entry/exit stations of each collider along the long
              // axis → square crosscuts there (angled seat cuts not attempted).
              const dirX = new THREE.Vector3(1, 0, 0).applyQuaternion(mesh.quaternion);
              const origin = mesh.position;
              const intervals: [number, number][] = [];
              for (const cs of colliders) {
                const pts = solidIntersectionPoints(mySolid, cs);
                if (pts.length === 0) continue;
                let lo = Infinity, hi = -Infinity;
                for (const p of pts) {
                  const u = new THREE.Vector3(p[0], p[1], p[2]).sub(origin).dot(dirX) * 12 + item.lengthIn / 2;
                  if (u < lo) lo = u;
                  if (u > hi) hi = u;
                }
                lo = Math.max(0, Math.round(lo * 16) / 16);
                hi = Math.min(item.lengthIn, Math.round(hi * 16) / 16);
                if (hi - lo >= 0.125) intervals.push([lo, hi]);
              }
              // Wave 5: the truss model itself. Five rays along the member
              // (centreline + the four cross-section corners inset 1/8") are
              // cast against the visible model meshes; entry/exit crossings
              // pair into penetration intervals. Ray sampling — an
              // approximation, not an exact solid-vs-mesh intersection.
              if (modelTargets.length > 0) {
                const d = lumberDims(item.size);
                const Lft = item.lengthIn / 12;
                const hy = Math.max(d.depth / 24 - 0.125 / 12, 0.002);
                const hz = Math.max(d.thick / 24 - 0.125 / 12, 0.002);
                const PAD = 0.05; // start the ray just before the board (feet)
                probe.far = Lft + PAD * 2;
                for (const [ly, lz] of [[0, 0], [-hy, -hz], [hy, -hz], [hy, hz], [-hy, hz]] as const) {
                  probe.set(mesh.localToWorld(new THREE.Vector3(-Lft / 2 - PAD, ly, lz)), dirX);
                  const stations = probe.intersectObjects(modelTargets, false).map(h => (h.distance - PAD) * 12);
                  for (const iv of pairStationsToIntervals(stations, item.lengthIn)) intervals.push(iv);
                }
              }
              const merged = mergeIntervals(intervals);
              if (merged.length === 0) { setAutocut({ kind: 'none' }); return; }
              const d = lumberDims(item.size);
              for (const [a, b] of merged) {
                const g = new THREE.BoxGeometry((b - a) / 12, d.depth / 12 + 0.015, d.thick / 12 + 0.015);
                const m = new THREE.Mesh(g, autocutMat);
                m.renderOrder = 996;
                m.position.set(((a + b) / 2) / 12 - item.lengthIn / 24, 0, 0);
                mesh.add(m);
                autocutPreview.push(m);
              }
              autocutPlan = { itemId: item.id, merged };
              const removeIn = merged.reduce((s2, [a, b]) => s2 + (b - a), 0);
              setAutocut({ kind: 'lumber', segments: merged.length, removeIn });
              return;
            }

            // Sheet: punch a bounding rectangle (+1/8" clearance each side)
            // around each penetrating solid via the existing cut-out machinery.
            // Wave 6: snapshot up front; pushed to the undo stack only when a
            // cut actually lands (punchRect mutates item.cuts in place).
            const undoSnap = cloneSketch();
            let made = 0, skipped = 0;
            for (const cs of colliders) {
              const pts = solidIntersectionPoints(mySolid, cs);
              if (pts.length === 0) { skipped++; continue; }
              let u0 = Infinity, v0 = Infinity, u1 = -Infinity, v1 = -Infinity;
              for (const p of pts) {
                const local = mesh.worldToLocal(new THREE.Vector3(p[0], p[1], p[2]));
                const u = local.x * 12 + item.lengthIn / 2;
                const v = item.widthIn / 2 - local.z * 12;
                if (u < u0) u0 = u;
                if (u > u1) u1 = u;
                if (v < v0) v0 = v;
                if (v > v1) v1 = v;
              }
              if (punchRect(item, u0, v0, u1, v1)) made++; else skipped++;
            }
            // Wave 5: the truss model. Build a ~1" covered-cell mask of the
            // sheet face: every visible model triangle (near the sheet) is
            // transformed into sheet space, clipped to the slab's interior
            // (|y| within the thickness less a ~1/40" flush margin, so
            // face-to-face contact never registers), and its in-slab part is
            // rasterized onto the 1" grid. Each 8-connected component of the
            // mask punches one bounding-rect cut-out (+1/8" clearance each
            // side). A member passing fully inside the slab still shows via
            // its side faces, and no ray parity is involved. Approximation:
            // coverage is per-triangle bounding boxes on a 1" grid, not an
            // exact solid intersection.
            if (modelTargets.length > 0) {
              const nu = Math.max(1, Math.round(item.lengthIn));
              const nv = Math.max(1, Math.round(item.widthIn));
              const du = item.lengthIn / nu, dv = item.widthIn / nv;
              const mask: boolean[] = new Array(nu * nv).fill(false);
              const inv = mesh.matrixWorld.clone().invert();
              const yLim = item.thickIn / 24 - 0.002;
              const sheetBox = new THREE.Box3().setFromObject(mesh).expandByScalar(0.05);
              const va = new THREE.Vector3(), vb = new THREE.Vector3(), vc = new THREE.Vector3();
              for (const m2 of modelTargets) {
                const g2 = m2.geometry as import('three').BufferGeometry;
                if (!g2.boundingBox) g2.computeBoundingBox();
                if (!g2.boundingBox || !g2.boundingBox.clone().applyMatrix4(m2.matrixWorld).intersectsBox(sheetBox)) continue;
                const toLocal = new THREE.Matrix4().multiplyMatrices(inv, m2.matrixWorld);
                const attr = g2.getAttribute('position');
                const idx = g2.getIndex();
                const triCount = (idx ? idx.count : attr.count) / 3;
                for (let t2 = 0; t2 < triCount; t2++) {
                  const ia = idx ? idx.getX(t2 * 3) : t2 * 3;
                  const ib = idx ? idx.getX(t2 * 3 + 1) : t2 * 3 + 1;
                  const ic = idx ? idx.getX(t2 * 3 + 2) : t2 * 3 + 2;
                  va.fromBufferAttribute(attr, ia).applyMatrix4(toLocal);
                  vb.fromBufferAttribute(attr, ib).applyMatrix4(toLocal);
                  vc.fromBufferAttribute(attr, ic).applyMatrix4(toLocal);
                  let poly: [number, number, number][] = [[va.x, va.y, va.z], [vb.x, vb.y, vb.z], [vc.x, vc.y, vc.z]];
                  for (const sgn of [1, -1] as const) {
                    const next: [number, number, number][] = [];
                    for (let i = 0; i < poly.length; i++) {
                      const A = poly[i], B = poly[(i + 1) % poly.length];
                      const fa = yLim - sgn * A[1], fb = yLim - sgn * B[1];
                      if (fa >= 0) next.push(A);
                      if ((fa > 0 && fb < 0) || (fa < 0 && fb > 0)) {
                        const tt = fa / (fa - fb);
                        next.push([A[0] + tt * (B[0] - A[0]), A[1] + tt * (B[1] - A[1]), A[2] + tt * (B[2] - A[2])]);
                      }
                    }
                    poly = next;
                    if (poly.length === 0) break;
                  }
                  if (poly.length < 3) continue;
                  let u0 = Infinity, v0 = Infinity, u1 = -Infinity, v1 = -Infinity;
                  for (const p of poly) {
                    const u = p[0] * 12 + item.lengthIn / 2;
                    const v = item.widthIn / 2 - p[2] * 12;
                    if (u < u0) u0 = u;
                    if (u > u1) u1 = u;
                    if (v < v0) v0 = v;
                    if (v > v1) v1 = v;
                  }
                  const i0 = Math.max(0, Math.floor(u0 / du)), i1 = Math.min(nu - 1, Math.floor(u1 / du));
                  const j0 = Math.max(0, Math.floor(v0 / dv)), j1 = Math.min(nv - 1, Math.floor(v1 / dv));
                  for (let j = j0; j <= j1; j++) {
                    for (let i = i0; i <= i1; i++) mask[j * nu + i] = true;
                  }
                }
              }
              for (const c of maskComponents(mask, nu, nv)) {
                if (punchRect(item, c.i0 * du, c.j0 * dv, (c.i1 + 1) * du, (c.j1 + 1) * dv)) made++; else skipped++;
              }
            }
            if (made > 0) {
              pushUndo(undoSnap);
              rebuildItemGeometry(item);
              setSelected({ ...item });
              scheduleSave();
            }
            setAutocut(made + skipped > 0 ? { kind: 'sheet', made, skipped } : { kind: 'none' });
          }),
          autocutConfirm: () => {
            const plan = autocutPlan;
            clearAutocut();
            if (!plan) return;
            const item = itemById(plan.itemId);
            const mesh = itemMeshes.get(plan.itemId);
            if (!item || !mesh || isSheet(item)) return;
            const spans = complementSpans(plan.merged, item.lengthIn);
            const dirX = new THREE.Vector3(1, 0, 0).applyQuaternion(mesh.quaternion);
            const center = new THREE.Vector3().fromArray(item.pos);
            const pieces: LumberItem[] = spans.map(([a, b]) => {
              const off = ((a + b) / 2) / 12 - item.lengthIn / 24;
              return {
                ...item, kind: 'lumber', id: rid(),
                lengthIn: Math.round((b - a) * 16) / 16,
                e0: a < 1 / 32 ? item.e0 : undefined,
                e1: b > item.lengthIn - 1 / 32 ? item.e1 : undefined,
                pos: center.clone().add(dirX.clone().multiplyScalar(off)).toArray() as V3,
                rot: [...item.rot] as V3,
              };
            });
            mutate(() => {
              removeItem(item.id, false);
              for (const p of pieces) { sketchRef.current.members.push(p); buildItemMesh(p); }
              selectItem(pieces.length ? pieces[0].id : null);
              scheduleSave();
            });
          },
          autocutCancel: clearAutocut,
          setCutAngles: (miter, bevel) => {
            pendingAngles = { m: Math.max(0, Math.min(60, miter || 0)), b: Math.max(0, Math.min(60, bevel || 0)) };
            updateCutPreview();
          },
          confirmCut: () => {
            if (!pendingCut) return;
            const item = itemById(pendingCut.itemId);
            if (!item) { clearPendingCut(); return; }
            const KERF = 0.25; // visual gap (inches) so the cut reads on screen
            const rotE = new THREE.Euler(item.rot[0], item.rot[1], item.rot[2]);
            const center = new THREE.Vector3().fromArray(item.pos);
            const dirX = new THREE.Vector3(1, 0, 0).applyEuler(rotE);

            if (pendingCut.type === 'lumber' && !isSheet(item)) {
              // Mitre/bevel/square cut: split into two solids with
              // complementary angled ends. Lengths are long-point lengths.
              const d = lumberDims(item.size);
              const Dft = d.depth / 12, Tft = d.thick / 12;
              const e: EndCut = { m: pendingAngles.m, b: pendingAngles.b };
              const { maxO, minO } = endOffsets(Dft, Tft, e);
              const span = (maxO - minO) * 12;
              const uIn = pendingCut.uIn;
              const LA = Math.round((uIn + maxO * 12) * 16) / 16;
              const LB = Math.round((item.lengthIn - uIn - minO * 12) * 16) / 16;
              const o0 = endOffsets(Dft, Tft, item.e0);
              const o1 = endOffsets(Dft, Tft, item.e1);
              if (LA < span + (o0.maxO - o0.minO) * 12 + 0.5 || LB < span + (o1.maxO - o1.minO) * 12 + 0.5) {
                clearPendingCut();
                return;
              }
              const Lft = item.lengthIn / 12;
              const xc = uIn / 12 - Lft / 2;
              const cA = (-Lft / 2 + (xc + maxO)) / 2;
              const cB = ((xc + minO) + Lft / 2) / 2;
              const posA = center.clone().add(dirX.clone().multiplyScalar(cA - KERF / 12));
              const posB = center.clone().add(dirX.clone().multiplyScalar(cB + KERF / 12));
              const square = isSquare(e);
              const a: LumberItem = { ...item, kind: 'lumber', id: rid(), lengthIn: LA, e1: square ? undefined : e, pos: posA.toArray() as V3, rot: [...item.rot] as V3 };
              const b: LumberItem = { ...item, kind: 'lumber', id: rid(), lengthIn: LB, e0: square ? undefined : e, pos: posB.toArray() as V3, rot: [...item.rot] as V3 };
              clearPendingCut();
              mutate(() => {
                removeItem(item.id, false);
                sketchRef.current.members.push(a, b);
                buildItemMesh(a);
                buildItemMesh(b);
                selectItem(a.id);
                scheduleSave();
              });
              return;
            }

            if (pendingCut.type === 'sheet' && isSheet(item)) {
              // Split the sheet outline along the tapped line (half-plane clip)
              const [u1, v1, u2, v2] = pendingCut.pts;
              const base: Pt[] = item.poly ?? [[0, 0], [item.lengthIn, 0], [item.lengthIn, item.widthIn], [0, item.widthIn]];
              const dirZ = new THREE.Vector3(0, 0, 1).applyEuler(rotE);
              const halves = ([1, -1] as const).map(side => clipPolyHalfPlane(base, [u1, v1], [u2, v2], side));
              if (halves.some(h => h.length < 3 || Math.abs(polySignedArea(h)) < 1)) { clearPendingCut(); return; }
              // Kerf: push the pieces apart along the cut line's normal
              const gl = Math.hypot(u2 - u1, v2 - v1);
              const g: Pt = [-(v2 - v1) / gl, (u2 - u1) / gl];
              const gWorld = dirX.clone().multiplyScalar(g[0]).add(dirZ.clone().multiplyScalar(-g[1])).normalize();
              const pieces: SheetItem[] = [];
              halves.forEach((poly, idx) => {
                const bb = polyBounds(poly);
                if (bb.w < 0.5 || bb.h < 0.5) return;
                const shifted: Pt[] = poly.map(p => [Math.round((p[0] - bb.x) * 64) / 64, Math.round((p[1] - bb.y) * 64) / 64]);
                // Holes crossed by the line go to the side holding their centre
                const cuts = item.cuts
                  .filter(c => pointInPoly([c.x + c.w / 2, c.y + c.h / 2], poly))
                  .map(c => ({ x: c.x - bb.x, y: c.y - bb.y, w: c.w, h: c.h }));
                const du = bb.x + bb.w / 2 - item.lengthIn / 2;
                const dv = bb.y + bb.h / 2 - item.widthIn / 2;
                const pos = center.clone()
                  .add(dirX.clone().multiplyScalar(du / 12))
                  .add(dirZ.clone().multiplyScalar(-dv / 12))
                  .add(gWorld.clone().multiplyScalar(((idx === 0 ? 1 : -1) * KERF) / 12));
                pieces.push({
                  ...item, id: rid(),
                  lengthIn: Math.round(bb.w * 16) / 16, widthIn: Math.round(bb.h * 16) / 16,
                  poly: polyIsRect(shifted, bb.w, bb.h) ? undefined : shifted,
                  cuts, pos: pos.toArray() as V3, rot: [...item.rot] as V3,
                });
              });
              if (pieces.length < 2) { clearPendingCut(); return; }
              clearPendingCut();
              mutate(() => {
                removeItem(item.id, false);
                for (const p of pieces) { sketchRef.current.members.push(p); buildItemMesh(p); }
                selectItem(pieces[0].id);
                scheduleSave();
              });
            }
          },
        };

        api.current = {
          fit, top, front,
          zoom: f => { camera.position.sub(controls.target).multiplyScalar(f).add(controls.target); controls.update(); },
          setVisible: (type, visible) => {
            const grp = groupByType.get(type);
            if (grp) grp.visible = visible;
            // The catch-all bucket covers any type without its own toggle
            if (layers.find(l => l.key === type)?.catchAll) {
              for (const [t, g] of groupByType) {
                if (!layers.some(l => l.key === t)) g.visible = visible;
              }
            }
          },
          sketch,
          dispose: () => {
            cancelAnimationFrame(raf);
            delete (window as unknown as Record<string, unknown>).__mmdlTapWorld;
            window.removeEventListener('resize', onResize);
            renderer.domElement.removeEventListener('pointerdown', onDown);
            renderer.domElement.removeEventListener('pointermove', onMove);
            renderer.domElement.removeEventListener('pointerup', onUp);
            renderer.domElement.removeEventListener('pointercancel', onCancel);
            controls.dispose();
            renderer.dispose();
            if (renderer.domElement.parentElement === mount) mount.removeChild(renderer.domElement);
            if (labelLayer.parentElement === mount) mount.removeChild(labelLayer);
            setSelected(null);
            setCutPending(null);
            setCutoutStage(0);
            setAutocut(null);
            setLivePos(null);
            setRunStage(0);
            setGenPlan(null);
            setGenStage(0);
          },
        };
        setLoading(false);
      } catch {
        if (!disposed) { setLoading(false); onError?.('3D view is not available for this model.'); }
      }
    })();
    return () => { disposed = true; api.current?.dispose(); api.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelKey, active]);

  const toggleLayer = (key: string) => {
    setHidden(prev => {
      const next = new Set(prev);
      const nowHidden = !next.has(key);
      if (nowHidden) next.add(key); else next.delete(key);
      api.current?.setVisible(key, !nowHidden);
      return next;
    });
  };

  const toggleSketch = () => {
    setSketchOn(v => {
      const on = !v;
      if (!on) {
        api.current?.sketch.select(null);
        setMeasureOn(false);
        setRunOn(false);
        setGenOn(false);
        setTool('none');
        setCheatOpen(false);
      } else {
        setPicked(null);
      }
      return on;
    });
  };

  // Wave 5: generate the layout children from the panel values.
  const runLayout = () => {
    const spacing = laySpacing === 'custom' ? (parseFeetInches(laySpacingCustom) ?? 0) : parseFloat(laySpacing);
    const startIn = Math.max(0, parseFeetInches(layStart) ?? 0);
    const lengthIn = parseFeetInches(layLen);
    if (!spacing || spacing < 1 || lengthIn === null || lengthIn < 1) return;
    const pitchN = parseFloat(layPitch) || 0;
    const count = layAuto ? null : Math.max(1, Math.min(200, parseInt(layCount, 10) || 1));
    api.current?.sketch.layoutGenerate({
      size: laySize,
      spacingIn: Math.round(spacing * 16) / 16,
      startIn: Math.round(startIn * 16) / 16,
      count,
      lengthIn: Math.round(lengthIn * 16) / 16,
      pitchN,
      face: layFace,
    });
  };
  const layPitchDeg = Math.round((Math.atan2(parseFloat(layPitch) || 0, 12) * 180 / Math.PI) * 10) / 10;

  const handleAdd = () => {
    const len = Math.min(960, Math.max(1, parseFeetInches(addLen) ?? 96));
    if (addKind === 'ply') {
      const w = Math.min(960, Math.max(1, parseFeetInches(addWidth) ?? 48));
      api.current?.sketch.addSheet(addThick, w, len);
    } else {
      api.current?.sketch.addLumber(addKind, len);
    }
  };

  const applyLength = () => {
    const v = parseFeetInches(lenDraft);
    if (v !== null && v >= 1 && v <= 960) api.current?.sketch.setLength(Math.round(v * 16) / 16);
    else if (selected) setLenDraft(formatInches(selected.lengthIn));
  };
  const applyWidth = () => {
    const v = parseFeetInches(widthDraft);
    if (v !== null && v >= 1 && v <= 960) api.current?.sketch.setWidth(Math.round(v * 16) / 16);
    else if (selected && selected.kind === 'sheet') setWidthDraft(formatInches(selected.widthIn));
  };

  const AXES = ['x', 'y', 'z'] as const;
  const applyPos = (i: 0 | 1 | 2) => {
    const v = parseSignedFeetInches(posDraft[i]); // inches
    if (v !== null && Math.abs(v) <= 120000) api.current?.sketch.setPosition(AXES[i], v / 12);
    else if (selected) setPosDraft(d => { const n = [...d] as [string, string, string]; n[i] = formatSignedFeet(selected.pos[i]); return n; });
  };
  const applyRot = (i: 0 | 1 | 2) => {
    const v = parseFloat(rotDraft[i]);
    if (isFinite(v)) api.current?.sketch.setRotationDeg(AXES[i], v);
    else if (selected) setRotDraft(d => { const n = [...d] as [string, string, string]; n[i] = String(Math.round(((selected.rot[i] * 180) / Math.PI) * 10) / 10); return n; });
  };

  // --- Wave 6: keyboard layer (desktop power use; active only while the 3D
  // tab is showing AND sketch mode is on; typing fields are left alone). ---
  const addLenRef = useRef<HTMLInputElement>(null);
  const lastLumber = useRef<LumberSizeKey>('2x4');
  useEffect(() => { if (addKind !== 'ply') lastLumber.current = addKind; }, [addKind]);
  const focusAddLen = () => setTimeout(() => { addLenRef.current?.focus(); addLenRef.current?.select(); }, 0);

  const cycleSelection = (dir: 1 | -1) => {
    const members = sketchRef.current.members;
    if (members.length === 0) return;
    const idx = members.findIndex(m => m.id === selected?.id);
    const next = idx === -1
      ? (dir === 1 ? 0 : members.length - 1)
      : (idx + dir + members.length) % members.length;
    api.current?.sketch.select(members[next].id);
  };

  // Esc cascade: close the cheat sheet → cancel whatever tool is mid-flight →
  // put the piece down. One level per press. Returns false when there was
  // nothing left to do — that press falls through to the viewer modal's own
  // Esc-to-close (deliberate conflict resolution: one more Esc closes it).
  const escCascade = (): boolean => {
    if (cheatOpen) { setCheatOpen(false); return true; }
    if (measureOn || runOn || genOn || tool !== 'none' || cutPending || autocut) {
      setMeasureOn(false);
      setRunOn(false);
      setGenOn(false);
      setTool('none');
      api.current?.sketch.cancelCut();
      api.current?.sketch.autocutCancel();
      api.current?.sketch.genCancel();
      return true;
    }
    if (selected) { api.current?.sketch.select(null); return true; }
    return false;
  };

  const STEP_CYCLE = [1 / 16, 0.25, 1, 6, 12];
  const keyFnRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyFnRef.current = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    if (firstHelp || loading) return;
    const lower = e.key.toLowerCase();
    const mod = e.ctrlKey || e.metaKey;

    if (mod && !e.altKey) {
      if (lower === 'z') { e.preventDefault(); if (e.shiftKey) doRedo(); else doUndo(); }
      else if (lower === 'y') { e.preventDefault(); doRedo(); }
      else if (lower === 's') { e.preventDefault(); void forceSave(); }
      else if (lower === 'd') { e.preventDefault(); api.current?.sketch.duplicate(); }
      return; // leave every other browser combo alone
    }
    if (e.key === '?' || (lower === 'k' && !e.altKey)) { e.preventDefault(); setCheatOpen(v => !v); return; }
    if (e.key === 'Escape') { if (escCascade()) e.preventDefault(); return; }
    if (cheatOpen) return; // overlay showing: only ? / K / Esc act
    if (e.key === 'Tab') { e.preventDefault(); cycleSelection(e.shiftKey ? -1 : 1); return; }
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); api.current?.sketch.remove(); return; }

    // Movement: the current nudge step; Shift = 12× the step; Alt = 1/16".
    const eff = e.altKey ? 1 / 16 : e.shiftKey ? step * 12 : step;
    switch (e.key) {
      case 'ArrowLeft': e.preventDefault(); api.current?.sketch.nudgeCam(-1, 0, eff); return;
      case 'ArrowRight': e.preventDefault(); api.current?.sketch.nudgeCam(1, 0, eff); return;
      case 'ArrowUp': e.preventDefault(); api.current?.sketch.nudgeCam(0, 1, eff); return;
      case 'ArrowDown': e.preventDefault(); api.current?.sketch.nudgeCam(0, -1, eff); return;
      case 'PageUp': e.preventDefault(); api.current?.sketch.nudge('y', 1, eff); return;
      case 'PageDown': e.preventDefault(); api.current?.sketch.nudge('y', -1, eff); return;
    }
    if (lower === 'q' || lower === 'e') {
      e.preventDefault();
      api.current?.sketch.nudge('y', lower === 'q' ? 1 : -1, eff);
      return;
    }
    if (e.key === '[' || e.key === ']') {
      e.preventDefault();
      const dir = e.key === '[' ? -1 : 1;
      const idx = STEP_CYCLE.reduce((best, v, i) => (Math.abs(v - step) < Math.abs(STEP_CYCLE[best] - step) ? i : best), 0);
      setStep(STEP_CYCLE[(idx + dir + STEP_CYCLE.length) % STEP_CYCLE.length]);
      return;
    }
    // Rotation: the fine-turn step (respects Snap 15°); Shift = 90°; Alt = 1°.
    const rotDeg = e.altKey ? 1 : e.shiftKey ? 90 : snap15 ? 15 : 5;
    const rotSnap = snap15 && !e.altKey;
    if (e.key === ',' || e.key === '<') { e.preventDefault(); api.current?.sketch.yawWorld(-rotDeg, rotSnap); return; }
    if (e.key === '.' || e.key === '>') { e.preventDefault(); api.current?.sketch.yawWorld(rotDeg, rotSnap); return; }
    if (e.key === ';' || e.key === ':') { e.preventDefault(); api.current?.sketch.rotate('x', -rotDeg, rotSnap); return; }
    if (e.key === "'" || e.key === '"') { e.preventDefault(); api.current?.sketch.rotate('x', rotDeg, rotSnap); return; }
    if (e.key >= '1' && e.key <= '7' && !e.altKey) {
      e.preventDefault();
      setAddKind(LUMBER_SIZES[Number(e.key) - 1].key);
      return;
    }
    if (e.altKey || e.shiftKey) return;
    switch (lower) {
      case 'b': e.preventDefault(); setAddKind(k => (k === 'ply' ? lastLumber.current : k)); focusAddLen(); return;
      case 'p': e.preventDefault(); setAddKind('ply'); focusAddLen(); return;
      case 'm': e.preventDefault(); setMeasureOn(v => { const on = !v; if (on) { setRunOn(false); setGenOn(false); } return on; }); return;
      case 'r': e.preventDefault(); setRunOn(v => { const on = !v; if (on) { setMeasureOn(false); setGenOn(false); } return on; }); return;
      case 'c': if (selected) { e.preventDefault(); setTool(t2 => (t2 === 'cut' ? 'none' : 'cut')); } return;
      case 'o': if (selected?.kind === 'sheet') { e.preventDefault(); setTool(t2 => (t2 === 'cutout' ? 'none' : 'cutout')); } return;
      case 'l': if (selected) { e.preventDefault(); setTool(t2 => (t2 === 'lay' ? 'none' : 'lay')); } return;
      case 'n': if (selected) { e.preventDefault(); setTool(t2 => (t2 === 'line' ? 'none' : 'line')); } return;
      case 'y': if (selected && selected.kind !== 'sheet') { e.preventDefault(); setTool(t2 => (t2 === 'layout' ? 'none' : 'layout')); } return;
      case 'a': if (selected) { e.preventDefault(); api.current?.sketch.autocutRun(); } return;
      case 'g': e.preventDefault(); setGlide(v => { const on = !v; if (on) setVertDrag(false); return on; }); return;
      case 'v': e.preventDefault(); setVertDrag(v => { const on = !v; if (on) setGlide(false); return on; }); return;
      case 't': e.preventDefault(); setMagnet(v => !v); return;
      case 'x': e.preventDefault(); toggleSketch(); return;
    }
  };

  useEffect(() => {
    if (!active || !sketchOn) return;
    // Capture phase + stopPropagation on consumed keys: hosts (the
    // document-viewer modal's Esc-to-close, the drafting page's own tool
    // hotkeys) listen at the document/window level, and consumed keys must
    // never reach handlers like that (or scroll the page).
    const h = (e: KeyboardEvent) => {
      keyFnRef.current(e);
      if (e.defaultPrevented) e.stopPropagation();
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [active, sketchOn]);

  const saveLabel =
    saveState === 'saving' ? 'Saving…'
    : saveState === 'saved' ? 'Saved'
    : saveState === 'dirty' ? 'Unsaved'
    : saveState === 'error' ? 'Save failed' : '';

  const smallBtn = (label: string, onClick: () => void, opts?: { active?: boolean; danger?: boolean; icon?: string; title?: string }) => (
    <button
      onClick={onClick}
      title={opts?.title ?? label}
      className={`h-11 min-w-11 px-2 rounded-lg border shadow-sm text-xs font-bold flex items-center justify-center gap-1 ${
        opts?.active ? 'bg-primary text-white border-primary'
        : opts?.danger ? 'bg-white text-red-700 border-red-200 hover:bg-red-50'
        : 'bg-white/95 text-gray-700 border-gray-300 hover:bg-white'
      }`}
    >
      {opts?.icon && <span className="material-symbols-outlined text-lg">{opts.icon}</span>}
      {label}
    </button>
  );

  const selectedIsSheet = selected?.kind === 'sheet';
  const selectedLabel = !selected ? '' : selected.kind === 'sheet'
    ? `Ply ${PLY_THICKNESSES.find(t => Math.abs(t.thick - selected.thickIn) < 1e-6)?.label ?? formatInches(selected.thickIn)}`
    : selected.size;

  // Stay MOUNTED while inactive (the sketch, undo stacks and loaded-flag all
  // live in refs here) — the host toggles `active` instead of unmounting.
  if (!active) return null;

  return (
    <div className="absolute inset-0 rounded-lg overflow-hidden border border-gray-200">
            <div ref={mountRef} className="absolute inset-0" />

            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-gray-600 text-sm z-10">
                <span className="material-symbols-outlined animate-spin mr-2">progress_activity</span>
                Building the 3D model…
              </div>
            )}

            {/* First-time help overlay */}
            {firstHelp && !loading && (
              <div className="absolute inset-0 z-20 bg-white/95 overflow-y-auto">
                <HelpContent onDone={dismissFirstHelp} variant={helpVariant} />
              </div>
            )}

            {/* Control bars — big touch targets */}
            {!firstHelp && (
              <div className="absolute top-2 left-2 right-2 z-10 flex flex-col gap-1.5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => api.current?.fit()} className="h-10 px-3 rounded-lg bg-white/95 border border-gray-300 shadow-sm text-sm font-semibold text-gray-700 hover:bg-white flex items-center gap-1">
                    <span className="material-symbols-outlined text-lg">fit_screen</span>Fit
                  </button>
                  <button onClick={() => api.current?.top()} className="h-10 px-3 rounded-lg bg-white/95 border border-gray-300 shadow-sm text-sm font-semibold text-gray-700 hover:bg-white flex items-center gap-1">
                    <span className="material-symbols-outlined text-lg">vertical_align_bottom</span>Top
                  </button>
                  <button onClick={() => api.current?.front()} className="h-10 px-3 rounded-lg bg-white/95 border border-gray-300 shadow-sm text-sm font-semibold text-gray-700 hover:bg-white flex items-center gap-1">
                    <span className="material-symbols-outlined text-lg">home</span>Front
                  </button>
                  <div className="flex rounded-lg overflow-hidden border border-gray-300 shadow-sm">
                    <button onClick={() => api.current?.zoom(0.8)} className="h-10 w-11 bg-white/95 hover:bg-white text-gray-700 flex items-center justify-center" title="Zoom in">
                      <span className="material-symbols-outlined text-lg">add</span>
                    </button>
                    <button onClick={() => api.current?.zoom(1.25)} className="h-10 w-11 bg-white/95 hover:bg-white text-gray-700 border-l border-gray-300 flex items-center justify-center" title="Zoom out">
                      <span className="material-symbols-outlined text-lg">remove</span>
                    </button>
                  </div>
                  <button
                    onClick={toggleSketch}
                    className={`h-10 px-3 rounded-lg border shadow-sm text-sm font-bold flex items-center gap-1 ${
                      sketchOn ? 'bg-primary text-white border-primary' : 'bg-white/95 text-gray-700 border-gray-300 hover:bg-white'
                    }`}
                  >
                    <span className="material-symbols-outlined text-lg">carpenter</span>Sketch
                  </button>
                  <div className="ml-auto flex items-center gap-1.5 flex-wrap">
                    {layers.map(l => (
                      <button
                        key={l.key}
                        onClick={() => toggleLayer(l.key)}
                        className={`h-10 px-3 rounded-lg border shadow-sm text-sm font-semibold flex items-center gap-1.5 ${
                          hidden.has(l.key) ? 'bg-gray-100 text-gray-400 border-gray-200 line-through' : 'bg-white/95 text-gray-700 border-gray-300 hover:bg-white'
                        }`}
                      >
                        <span className="w-3 h-3 rounded-sm border border-black/10" style={{ background: l.swatch }} />
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sketch toolbar */}
                {sketchOn && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <select
                      value={addKind}
                      onChange={e => setAddKind(e.target.value as LumberSizeKey | 'ply')}
                      aria-label="Material"
                      className="h-11 rounded-lg border border-gray-300 bg-white/95 px-2 text-sm font-semibold text-gray-700 shadow-sm"
                    >
                      {LUMBER_SIZES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
                      <option value="ply">Plywood</option>
                    </select>
                    {addKind === 'ply' && (
                      <select
                        value={addThick}
                        onChange={e => setAddThick(Number(e.target.value))}
                        aria-label="Sheet thickness"
                        className="h-11 rounded-lg border border-gray-300 bg-white/95 px-2 text-sm font-semibold text-gray-700 shadow-sm"
                      >
                        {PLY_THICKNESSES.map(t => <option key={t.label} value={t.thick}>{t.label}</option>)}
                      </select>
                    )}
                    {addKind === 'ply' && (
                      <input
                        value={addWidth}
                        onChange={e => setAddWidth(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { handleAdd(); (e.target as HTMLInputElement).blur(); } }}
                        aria-label="Sheet width"
                        className="h-11 w-16 rounded-lg border border-gray-300 bg-white/95 px-2 text-sm font-semibold text-gray-700 shadow-sm"
                      />
                    )}
                    <input
                      ref={addLenRef}
                      value={addLen}
                      onChange={e => setAddLen(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { handleAdd(); (e.target as HTMLInputElement).blur(); } }}
                      aria-label="Length"
                      className="h-11 w-20 rounded-lg border border-gray-300 bg-white/95 px-2 text-sm font-semibold text-gray-700 shadow-sm"
                    />
                    <button onClick={handleAdd} className="h-11 px-3 rounded-lg bg-primary text-white text-sm font-bold shadow-sm hover:opacity-90 flex items-center gap-1">
                      <span className="material-symbols-outlined text-lg">add_box</span>Add
                    </button>
                    <button
                      onClick={() => setMeasureOn(v => { const on = !v; if (on) { setRunOn(false); setGenOn(false); } return on; })}
                      className={`h-11 px-3 rounded-lg border shadow-sm text-sm font-bold flex items-center gap-1 ${
                        measureOn ? 'bg-[#B8462E] text-white border-[#B8462E]' : 'bg-white/95 text-gray-700 border-gray-300 hover:bg-white'
                      }`}
                    >
                      <span className="material-symbols-outlined text-lg">straighten</span>Measure
                    </button>
                    <button
                      onClick={() => setRunOn(v => { const on = !v; if (on) { setMeasureOn(false); setGenOn(false); } return on; })}
                      title="Tap two points and lay boards flush between them (decking / strapping)"
                      className={`h-11 px-3 rounded-lg border shadow-sm text-sm font-bold flex items-center gap-1 ${
                        runOn ? 'bg-primary text-white border-primary' : 'bg-white/95 text-gray-700 border-gray-300 hover:bg-white'
                      }`}
                    >
                      <span className="material-symbols-outlined text-lg">view_week</span>Lay-run
                    </button>
                    <button
                      onClick={() => setGenOn(v => { const on = !v; if (on) { setMeasureOn(false); setRunOn(false); } return on; })}
                      title="Tap a roof plane and auto-build sheathing, purlins, sub fascia or a gable overhang ladder onto it"
                      className={`h-11 px-3 rounded-lg border shadow-sm text-sm font-bold flex items-center gap-1 ${
                        genOn ? 'bg-primary text-white border-primary' : 'bg-white/95 text-gray-700 border-gray-300 hover:bg-white'
                      }`}
                    >
                      <span className="material-symbols-outlined text-lg">auto_awesome</span>Generate
                    </button>
                    {measureCount > 0 && (
                      <button onClick={() => api.current?.sketch.clearMeasurements()} className="h-11 px-3 rounded-lg bg-white/95 border border-gray-300 shadow-sm text-sm font-semibold text-gray-700 hover:bg-white">
                        Clear tape
                      </button>
                    )}
                    <span className="ml-auto flex items-center gap-1.5">
                      {isDesktop && (
                        <button
                          onClick={() => setCheatOpen(v => !v)}
                          title="Keyboard shortcuts (press ? or K)"
                          className="h-8 px-2 rounded-md border border-gray-300 bg-white/95 text-[11px] font-bold text-gray-600 hover:bg-white flex items-center gap-1 shadow-sm"
                        >
                          <span className="material-symbols-outlined text-sm">keyboard</span>? for shortcuts
                        </button>
                      )}
                      {saveLabel && (
                        <span className={`text-xs font-bold px-2 py-1 rounded-md ${
                          saveState === 'saved' ? 'bg-green-100 text-green-800'
                          : saveState === 'error' ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'
                        }`}>
                          {saveLabel}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Bottom overlay: pick label / sketch hints / selection panel */}
            {!firstHelp && (
              <div className="absolute bottom-2 left-2 right-2 z-10 flex flex-col items-start gap-1.5 pointer-events-none">
                {/* Mitre / bevel controls for a pending lumber cut */}
                {sketchOn && tool === 'cut' && selected && selected.kind !== 'sheet' && (
                  <div className="bg-white/95 border border-gray-300 rounded-xl shadow-lg p-2 space-y-1.5 max-w-full pointer-events-auto">
                    {([['Mitre', cutMiter, setCutMiter], ['Bevel', cutBevel, setCutBevel]] as const).map(([label, val, set]) => (
                      <div key={label} className="flex items-center gap-1 flex-wrap">
                        <span className="text-[11px] font-semibold text-gray-500 w-10">{label}</span>
                        {[0, 15, 22.5, 30, 45].map(p => (
                          <button
                            key={p}
                            onClick={() => set(p)}
                            className={`h-10 min-w-10 px-1.5 rounded-lg border text-xs font-bold ${
                              val === p ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {p}°
                          </button>
                        ))}
                        <input
                          type="number" min={0} max={60} step={0.5} value={val}
                          onChange={e => set(Math.max(0, Math.min(60, Number(e.target.value) || 0)))}
                          aria-label={`${label} degrees`}
                          className="h-10 w-16 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Wave 5: layout panel (children along a base member) */}
                {sketchOn && tool === 'layout' && selected && selected.kind !== 'sheet' && (
                  <div className="bg-white/95 border border-gray-300 rounded-xl shadow-lg p-2 space-y-1.5 max-w-full pointer-events-auto">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-500">Layout</span>
                      <select
                        value={laySize}
                        onChange={e => setLaySize(e.target.value as LumberSizeKey)}
                        aria-label="Layout piece size"
                        className="h-10 rounded-lg border border-gray-300 bg-white px-1.5 text-xs font-semibold text-gray-700"
                      >
                        {LUMBER_SIZES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
                      </select>
                      <select
                        value={laySpacing}
                        onChange={e => setLaySpacing(e.target.value)}
                        aria-label="Spacing on centre"
                        className="h-10 rounded-lg border border-gray-300 bg-white px-1.5 text-xs font-semibold text-gray-700"
                      >
                        <option value="16">16" o.c.</option>
                        <option value="19.2">19.2" o.c.</option>
                        <option value="24">24" o.c.</option>
                        <option value="custom">Custom…</option>
                      </select>
                      {laySpacing === 'custom' && (
                        <input
                          value={laySpacingCustom}
                          onChange={e => setLaySpacingCustom(e.target.value)}
                          aria-label="Custom spacing"
                          className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                        />
                      )}
                      <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">Start
                        <input
                          value={layStart}
                          onChange={e => setLayStart(e.target.value)}
                          aria-label="Start offset"
                          className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                        />
                      </label>
                      <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">L
                        <input
                          value={layLen}
                          onChange={e => setLayLen(e.target.value)}
                          aria-label="Piece length"
                          className="h-10 w-16 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                        />
                      </label>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">Pitch
                        <input
                          type="number" step={0.5} value={layPitch}
                          onChange={e => setLayPitch(e.target.value)}
                          aria-label="Pitch rise per 12"
                          className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                        />
                      </label>
                      <span className="text-[11px] font-semibold text-gray-500">/12 = {layPitchDeg}°</span>
                      <button
                        onClick={() => setLayFace(f => (f + 1) % 4)}
                        className="h-10 px-2 rounded-lg border border-gray-300 bg-white text-xs font-bold text-gray-700"
                        title="Which face of the base the pieces butt onto (highlighted on the board)"
                      >
                        Face: {FACE_LABELS[layFace]}
                      </button>
                      <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">
                        <input type="checkbox" checked={layAuto} onChange={e => setLayAuto(e.target.checked)} /> Auto-fill
                      </label>
                      {!layAuto && (
                        <input
                          type="number" min={1} max={200} value={layCount}
                          onChange={e => setLayCount(e.target.value)}
                          aria-label="Count"
                          className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                        />
                      )}
                      <button onClick={runLayout} className="h-10 px-3 rounded-lg bg-primary text-white text-xs font-bold">Generate</button>
                    </div>
                    <div className="text-[11px] text-gray-500 px-1">
                      Pieces butt end-grain onto the highlighted face, centres at the spacing, tilted to the pitch about this board's length. They come out as ordinary pieces.
                    </div>
                  </div>
                )}

                {/* Wave 5: lay-run panel (boards between two tapped points) */}
                {sketchOn && runOn && (
                  <div className="bg-white/95 border border-gray-300 rounded-xl shadow-lg p-2 space-y-1.5 max-w-full pointer-events-auto">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-500">Lay-run</span>
                      {smallBtn('Single', () => setRunMode('single'), { active: runMode === 'single', title: 'One board along the tapped line' })}
                      {smallBtn('Fill', () => setRunMode('fill'), { active: runMode === 'fill', title: 'Boards side by side across the run, with a gap between them' })}
                      {smallBtn('O.C.', () => setRunMode('oc'), { active: runMode === 'oc', title: 'Boards across the run at an on-centre spacing' })}
                      <select
                        value={runSize}
                        onChange={e => setRunSize(e.target.value as LumberSizeKey)}
                        aria-label="Board size"
                        className="h-10 rounded-lg border border-gray-300 bg-white px-1.5 text-xs font-semibold text-gray-700"
                      >
                        {LUMBER_SIZES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
                      </select>
                      {smallBtn(runFlat ? 'Flat' : 'On edge', () => setRunFlat(v => !v), { title: 'Lay the boards flat or on edge' })}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {runMode === 'fill' && (
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">Gap
                          <input
                            value={runGap}
                            onChange={e => setRunGap(e.target.value)}
                            aria-label="Gap between boards"
                            className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                          />
                        </label>
                      )}
                      {runMode === 'oc' && (
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">O.C.
                          <input
                            value={runSpacing}
                            onChange={e => setRunSpacing(e.target.value)}
                            aria-label="On-centre spacing"
                            className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                          />
                        </label>
                      )}
                      <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">L
                        <input
                          value={runLen}
                          onChange={e => setRunLen(e.target.value)}
                          placeholder="span"
                          aria-label="Board length (blank = the tapped span)"
                          className="h-10 w-16 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                        />
                      </label>
                      <span className="text-[11px] font-semibold text-gray-600">
                        {runStage === 0 ? 'Tap where the run starts' : 'Now tap where the run ends'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Wave 7: Generate panel — roof-plane generators */}
                {sketchOn && genOn && (
                  <div className="bg-white/95 border border-gray-300 rounded-xl shadow-lg p-2 space-y-1.5 max-w-full pointer-events-auto">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-500">Generate</span>
                      {smallBtn('Sheathing', () => setGenKind('sheath'), { active: genKind === 'sheath', title: "Sheath the tapped roof plane with 4'×8' plywood — edge sheets cut to fit" })}
                      {smallBtn('Purlins', () => setGenKind('purlin'), { active: genKind === 'purlin', title: 'Purlins across the slope at an on-centre spacing' })}
                      {smallBtn('Sub fascia', () => setGenKind('fascia'), { active: genKind === 'fascia', title: 'A plumb board on edge along the eave (lowest edge) of the tapped plane' })}
                      {smallBtn('Gable ladder', () => setGenKind('ladder'), { active: genKind === 'ladder', title: 'Overhang ladder on a drop gable: lookouts across the rake, capped by a fly rafter' })}
                    </div>
                    {genKind === 'sheath' && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <select
                          value={genPlyThick}
                          onChange={e => setGenPlyThick(Number(e.target.value))}
                          aria-label="Sheathing thickness"
                          className="h-10 rounded-lg border border-gray-300 bg-white px-1.5 text-xs font-semibold text-gray-700"
                        >
                          {PLY_THICKNESSES.map(t => <option key={t.label} value={t.thick}>{t.label}</option>)}
                        </select>
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">
                          <input type="checkbox" checked={genPlyStagger} onChange={e => setGenPlyStagger(e.target.checked)} /> Stagger rows 4'
                        </label>
                        <span className="text-[11px] text-gray-500">Long side across the slope · 1/8" gaps · nothing over 4'×8'</span>
                      </div>
                    )}
                    {genKind === 'purlin' && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <select
                          value={genPurlinSize}
                          onChange={e => setGenPurlinSize(e.target.value as LumberSizeKey)}
                          aria-label="Purlin size"
                          className="h-10 rounded-lg border border-gray-300 bg-white px-1.5 text-xs font-semibold text-gray-700"
                        >
                          {LUMBER_SIZES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
                        </select>
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">O.C.
                          <input
                            value={genPurlinSpacing}
                            onChange={e => setGenPurlinSpacing(e.target.value)}
                            aria-label="Purlin spacing on centre"
                            className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                          />
                        </label>
                        {smallBtn(genPurlinFlat ? 'On flat' : 'On edge', () => setGenPurlinFlat(v => !v), { title: 'Lay the purlins flat on the plane or stand them on edge' })}
                        <span className="text-[11px] text-gray-500">Run across the slope · butt-spliced at 16'</span>
                      </div>
                    )}
                    {genKind === 'fascia' && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <select
                          value={genFasciaSize}
                          onChange={e => setGenFasciaSize(e.target.value as LumberSizeKey)}
                          aria-label="Sub fascia size"
                          className="h-10 rounded-lg border border-gray-300 bg-white px-1.5 text-xs font-semibold text-gray-700"
                        >
                          {LUMBER_SIZES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
                        </select>
                        <span className="text-[11px] text-gray-500">On edge, hung plumb along the eave · spliced at 16'</span>
                      </div>
                    )}
                    {genKind === 'ladder' && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <select
                          value={genLadderSize}
                          onChange={e => setGenLadderSize(e.target.value as LumberSizeKey)}
                          aria-label="Lookout size"
                          className="h-10 rounded-lg border border-gray-300 bg-white px-1.5 text-xs font-semibold text-gray-700"
                        >
                          {LUMBER_SIZES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
                        </select>
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">O.C.
                          <input
                            value={genLadderSpacing}
                            onChange={e => setGenLadderSpacing(e.target.value)}
                            aria-label="Lookout spacing on centre"
                            className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                          />
                        </label>
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">Overhang
                          <input
                            value={genLadderOverhang}
                            onChange={e => setGenLadderOverhang(e.target.value)}
                            aria-label="Overhang distance past the rake"
                            className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                          />
                        </label>
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">Inboard
                          <input
                            value={genLadderInboard}
                            onChange={e => setGenLadderInboard(e.target.value)}
                            aria-label="Lookout inboard length"
                            className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                          />
                        </label>
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">Start
                          <input
                            value={genLadderStart}
                            onChange={e => setGenLadderStart(e.target.value)}
                            aria-label="First lookout offset from the eave end"
                            className="h-10 w-14 rounded-lg border border-gray-300 px-1.5 text-xs font-semibold text-gray-800"
                          />
                        </label>
                      </div>
                    )}
                    <div className="text-[11px] font-semibold text-gray-600 px-1">
                      {genKind === 'ladder'
                        ? (genStage === 0
                            ? 'Tap the roof plane the ladder sits on — the drop gable is assumed under it'
                            : 'Now tap the rake (gable end) edge the ladder hangs over')
                        : 'Tap the roof plane to build on — a preview shows before anything is added'}
                    </div>
                  </div>
                )}

                {/* Wave 7: Generate preview chip */}
                {sketchOn && genPlan && (
                  <div className="flex items-center gap-2 bg-[#2C1810] text-white rounded-lg px-3 py-2 shadow-lg flex-wrap pointer-events-auto">
                    <span className="material-symbols-outlined text-lg text-[#C77D3F]">auto_awesome</span>
                    <span className="text-sm font-bold">{genPlan.label}</span>
                    {genPlan.ok && (
                      <button onClick={() => api.current?.sketch.genConfirm()} className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold">Generate ✓</button>
                    )}
                    <button onClick={() => api.current?.sketch.genCancel()} className="h-9 px-3 rounded-lg bg-white/15 text-white text-sm font-bold" title="Dismiss">✕</button>
                  </div>
                )}

                {/* Cut confirmation chip */}
                {sketchOn && cutPending && (
                  <div className="flex items-center gap-2 bg-[#2C1810] text-white rounded-lg px-3 py-2 shadow-lg flex-wrap pointer-events-auto">
                    <span className="material-symbols-outlined text-lg text-[#C77D3F]">content_cut</span>
                    {cutPending.kind === 'lumber' ? (
                      <span className="text-sm font-bold">
                        Cut at {formatInches(cutPending.uIn)} from the left long point
                        {(cutMiter > 0 || cutBevel > 0) ? ` — mitre ${cutMiter}° · bevel ${cutBevel}°` : ''}?
                      </span>
                    ) : (
                      <span className="text-sm font-bold">
                        Cut the sheet along this line{cutPending.angleDeg > 0 ? ` (${cutPending.angleDeg}° off square)` : ''}?
                      </span>
                    )}
                    <button onClick={() => api.current?.sketch.confirmCut()} className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold">Cut</button>
                    <button onClick={() => api.current?.sketch.cancelCut()} className="text-white/60 hover:text-white" title="Cancel cut">
                      <span className="material-symbols-outlined text-base">close</span>
                    </button>
                  </div>
                )}

                {/* Autocut result chip */}
                {sketchOn && autocut && (
                  <div className="flex items-center gap-2 bg-[#2C1810] text-white rounded-lg px-3 py-2 shadow-lg flex-wrap pointer-events-auto">
                    <span className="material-symbols-outlined text-lg text-[#C77D3F]">join_inner</span>
                    {autocut.kind === 'none' && (
                      <span className="text-sm font-bold">No collisions found — checked your other pieces and the visible model.</span>
                    )}
                    {autocut.kind === 'lumber' && (
                      <>
                        <span className="text-sm font-bold">
                          {autocut.segments === 1 ? 'Colliding segment' : `${autocut.segments} colliding segments`} shown in red
                          ({formatInches(autocut.removeIn)} total) — remove?
                        </span>
                        <button onClick={() => api.current?.sketch.autocutConfirm()} className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold">Remove ✓</button>
                        <button onClick={() => api.current?.sketch.autocutCancel()} className="h-9 px-3 rounded-lg bg-white/15 text-white text-sm font-bold">Keep ✕</button>
                      </>
                    )}
                    {autocut.kind === 'sheet' && (
                      <span className="text-sm font-bold">
                        Cut {autocut.made} opening{autocut.made === 1 ? '' : 's'} around colliding pieces and model members (1/8" clearance each side)
                        {autocut.skipped > 0 ? ` — ${autocut.skipped} skipped` : ''}.
                      </span>
                    )}
                    {autocut.kind !== 'lumber' && (
                      <button onClick={() => api.current?.sketch.autocutCancel()} className="text-white/60 hover:text-white" title="Dismiss">
                        <span className="material-symbols-outlined text-base">close</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Selection panel */}
                {sketchOn && selected && (
                  <div className="bg-white/95 border border-gray-300 rounded-xl shadow-lg p-2 space-y-1.5 max-w-full pointer-events-auto">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-bold text-gray-900 px-1">{selectedLabel}</span>
                      {selected.kind === 'sheet' && selected.poly ? (
                        <span className="text-xs font-semibold text-gray-600" title="This sheet has an angled cut — resize by cutting">
                          {formatInches(selected.lengthIn)} × {formatInches(selected.widthIn)}
                        </span>
                      ) : (
                        <label
                          className="flex items-center gap-1 text-[11px] font-semibold text-gray-500"
                          title={selected.kind !== 'sheet' && (!isSquare(selected.e0) || !isSquare(selected.e1)) ? 'Long point to long point' : 'Length'}
                        >
                          {selected.kind !== 'sheet' && (!isSquare(selected.e0) || !isSquare(selected.e1)) ? 'L (LP)' : 'L'}
                          <input
                            value={lenDraft}
                            onChange={e => setLenDraft(e.target.value)}
                            onBlur={applyLength}
                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                            className="h-11 w-20 rounded-lg border border-gray-300 px-2 text-sm font-semibold text-gray-800"
                            aria-label="Length"
                          />
                        </label>
                      )}
                      {selectedIsSheet && !(selected.kind === 'sheet' && selected.poly) && (
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">W
                          <input
                            value={widthDraft}
                            onChange={e => setWidthDraft(e.target.value)}
                            onBlur={applyWidth}
                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                            className="h-11 w-16 rounded-lg border border-gray-300 px-2 text-sm font-semibold text-gray-800"
                            aria-label="Width"
                          />
                        </label>
                      )}
                      {selectedIsSheet && (
                        <select
                          value={selected.kind === 'sheet' ? selected.thickIn : 0.5}
                          onChange={e => api.current?.sketch.setThick(Number(e.target.value))}
                          aria-label="Sheet thickness"
                          className="h-11 rounded-lg border border-gray-300 bg-white px-1.5 text-sm font-semibold text-gray-700"
                        >
                          {PLY_THICKNESSES.map(t => <option key={t.label} value={t.thick}>{t.label}</option>)}
                        </select>
                      )}
                      {smallBtn('', () => api.current?.sketch.duplicate(), { icon: 'content_copy', title: 'Duplicate' })}
                      {smallBtn('', () => api.current?.sketch.remove(), { icon: 'delete', danger: true, title: 'Delete' })}
                      {smallBtn('', () => api.current?.sketch.select(null), { icon: 'close', title: 'Done with this piece' })}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-500 w-9">Turn</span>
                      {smallBtn('X 90°', () => api.current?.sketch.rotate('x', 90), { title: 'Roll 90° about the length' })}
                      {smallBtn('Y 90°', () => api.current?.sketch.rotate('y', 90), { title: 'Turn 90° about the face normal' })}
                      {smallBtn('Z 90°', () => api.current?.sketch.rotate('z', 90))}
                      {smallBtn('−15°', () => api.current?.sketch.rotate('x', -15, snap15), { title: 'Roll −15° about the length' })}
                      {smallBtn('+15°', () => api.current?.sketch.rotate('x', 15, snap15), { title: 'Roll +15° about the length' })}
                      {smallBtn(snap15 ? '−15°' : '−5°', () => api.current?.sketch.rotate('y', snap15 ? -15 : -5, snap15), { title: 'Turn about the face normal' })}
                      {smallBtn(snap15 ? '+15°' : '+5°', () => api.current?.sketch.rotate('y', snap15 ? 15 : 5, snap15), { title: 'Turn about the face normal' })}
                      {smallBtn('Snap 15°', () => setSnap15(v => !v), { active: snap15, title: 'Fine turns land on clean 15° steps' })}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-500 w-9">Tools</span>
                      {smallBtn('Cut', () => setTool(t => t === 'cut' ? 'none' : 'cut'), { icon: 'content_cut', active: tool === 'cut', title: 'Cut this piece' })}
                      {selectedIsSheet && smallBtn('Cut-out', () => setTool(t => t === 'cutout' ? 'none' : 'cutout'), { icon: 'select', active: tool === 'cutout', title: 'Cut a rectangle out of the sheet' })}
                      {selectedIsSheet && selected.kind === 'sheet' && selected.cuts.length > 0 &&
                        smallBtn('Undo cut-out', () => api.current?.sketch.undoCutout(), { icon: 'undo' })}
                      {smallBtn('Lay', () => setTool(t => t === 'lay' ? 'none' : 'lay'), { icon: 'layers', active: tool === 'lay', title: 'Lay this piece flat on a surface' })}
                      {smallBtn('Line', () => setTool(t => t === 'line' ? 'none' : 'line'), { icon: 'polyline', active: tool === 'line', title: 'Run this piece between two points' })}
                      {!selectedIsSheet && smallBtn('Layout', () => setTool(t => t === 'layout' ? 'none' : 'layout'), { icon: 'view_column', active: tool === 'layout', title: 'Lay out pieces along this one — studs, lookouts or rafters at a spacing and pitch' })}
                      {smallBtn('Autocut', () => api.current?.sketch.autocutRun(), { icon: 'join_inner', title: 'Find model members and sketch pieces running through this one and cut around them' })}
                      {tool === 'line' && (
                        <button
                          onClick={() => setAutoLen(v => !v)}
                          className={`h-11 px-2 rounded-lg border shadow-sm text-xs font-bold ${
                            autoLen ? 'bg-primary text-white border-primary' : 'bg-white/95 text-gray-700 border-gray-300'
                          }`}
                          title="Stretch the piece to exactly the tapped span"
                        >
                          Auto-length
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-500 w-9">Move</span>
                      <select
                        value={step}
                        onChange={e => setStep(Number(e.target.value))}
                        aria-label="Nudge step"
                        className="h-11 rounded-lg border border-gray-300 bg-white px-1.5 text-sm font-semibold text-gray-700"
                      >
                        <option value={0.0625}>1/16"</option>
                        <option value={0.25}>1/4"</option>
                        <option value={1}>1"</option>
                        <option value={6}>6"</option>
                        <option value={12}>12"</option>
                      </select>
                      {smallBtn('', () => api.current?.sketch.nudge('x', -1, step), { icon: 'west', title: 'Move −X' })}
                      {smallBtn('', () => api.current?.sketch.nudge('x', 1, step), { icon: 'east', title: 'Move +X' })}
                      {smallBtn('', () => api.current?.sketch.nudge('y', 1, step), { icon: 'arrow_upward', title: 'Raise' })}
                      {smallBtn('', () => api.current?.sketch.nudge('y', -1, step), { icon: 'arrow_downward', title: 'Lower' })}
                      {smallBtn('', () => api.current?.sketch.nudge('z', -1, step), { icon: 'north', title: 'Move −Z' })}
                      {smallBtn('', () => api.current?.sketch.nudge('z', 1, step), { icon: 'south', title: 'Move +Z' })}
                      {smallBtn('', () => setVertDrag(v => { const on = !v; if (on) setGlide(false); return on; }), { icon: 'swap_vert', active: vertDrag, title: 'Up-down drag: dragging raises or lowers the piece instead of sliding it across the ground' })}
                      {smallBtn('Glide', () => setGlide(v => { const on = !v; if (on) setVertDrag(false); return on; }), { active: glide, title: 'Glide drag: while you drag, the piece rides flush on whatever surface is under your finger — slide a board across a roof plane' })}
                      {smallBtn('Magnet', () => setMagnet(v => !v), { active: magnet, title: 'Snap flush onto a nearby piece’s face while dragging (within 2")' })}
                      {smallBtn('Precise', () => setPreciseOpen(v => !v), { icon: 'tune', active: preciseOpen, title: 'Type an exact position and angles' })}
                    </div>
                    {preciseOpen && (
                      <>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[11px] font-semibold text-gray-500 w-9">Pos</span>
                          {AXES.map((ax, i) => (
                            <label key={ax} className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">
                              {ax === 'y' ? 'Y↑' : ax.toUpperCase()}
                              <input
                                value={posDraft[i]}
                                onChange={e => setPosDraft(d => { const n = [...d] as [string, string, string]; n[i] = e.target.value; return n; })}
                                onBlur={() => applyPos(i as 0 | 1 | 2)}
                                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                className="h-11 w-[4.5rem] rounded-lg border border-gray-300 px-1.5 text-sm font-semibold text-gray-800"
                                aria-label={`Position ${ax.toUpperCase()}`}
                              />
                            </label>
                          ))}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[11px] font-semibold text-gray-500 w-9">Rot°</span>
                          {AXES.map((ax, i) => (
                            <label key={ax} className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">
                              {ax.toUpperCase()}
                              <input
                                type="number" step={0.5}
                                value={rotDraft[i]}
                                onChange={e => setRotDraft(d => { const n = [...d] as [string, string, string]; n[i] = e.target.value; return n; })}
                                onBlur={() => applyRot(i as 0 | 1 | 2)}
                                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                className="h-11 w-16 rounded-lg border border-gray-300 px-1.5 text-sm font-semibold text-gray-800"
                                aria-label={`Rotation ${ax.toUpperCase()} degrees`}
                              />
                            </label>
                          ))}
                        </div>
                        {livePos && (
                          <div className="text-[11px] font-semibold text-gray-600 px-1" data-testid="live-pos">
                            at X {formatSignedFeet(livePos[0])} · Y↑ {formatSignedFeet(livePos[1])} · Z {formatSignedFeet(livePos[2])}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Hints / picked-part label */}
                {sketchOn && !selected && (
                  <div className="text-[11px] text-gray-600 bg-white/80 rounded px-2 py-1 pointer-events-auto">
                    {measureOn ? 'Tap two points to measure between them' : 'Add a piece with the bar above, or tap one to pick it up'}
                  </div>
                )}
                {sketchOn && selected && tool === 'cut' && !cutPending && (
                  <div className="text-[11px] text-gray-600 bg-white/80 rounded px-2 py-1 pointer-events-auto">
                    {selected.kind === 'sheet'
                      ? (cutStage === 0 ? 'Tap two points on the sheet — the cut runs along that line' : 'Now tap the second point of the cut line')
                      : 'Tap the piece where you want the saw cut — set mitre/bevel above'}
                  </div>
                )}
                {sketchOn && selected && tool === 'cutout' && (
                  <div className="text-[11px] text-gray-600 bg-white/80 rounded px-2 py-1 pointer-events-auto">
                    {cutoutStage === 0 ? 'Tap the first corner of the cut-out on the sheet' : 'Now tap the opposite corner'}
                  </div>
                )}
                {sketchOn && selected && tool === 'lay' && (
                  <div className="text-[11px] text-gray-600 bg-white/80 rounded px-2 py-1 pointer-events-auto">
                    Tap a surface (roof, wall, or another piece) — the {selected.kind === 'sheet' ? 'sheet' : 'board'} lays flat on it. Use Y 90° to spin it after.
                  </div>
                )}
                {sketchOn && selected && tool === 'line' && (
                  <div className="text-[11px] text-gray-600 bg-white/80 rounded px-2 py-1 pointer-events-auto">
                    {snapStage === 0 ? 'Tap two points (e.g. either end of a hip) — the piece runs between them' : 'Now tap the far end point'}
                  </div>
                )}
                {!sketchOn && (
                  picked ? (
                    <div className="flex items-center gap-2 bg-[#2C1810] text-white rounded-lg px-3 py-2 shadow-lg pointer-events-auto">
                      <span className="material-symbols-outlined text-lg text-[#C77D3F]">carpenter</span>
                      <span className="text-sm font-bold">{picked.name || '(unnamed)'}</span>
                      <span className="text-xs text-white/70">{picked.type}</span>
                      <button onClick={() => setPicked(null)} className="ml-1 text-white/60 hover:text-white">
                        <span className="material-symbols-outlined text-base">close</span>
                      </button>
                    </div>
                  ) : (
                    <div className="text-[11px] text-gray-600 bg-white/80 rounded px-2 py-1 pointer-events-auto">
                      Tap any part to see its label
                    </div>
                  )
                )}
              </div>
            )}

            {/* Wave 6: keyboard shortcut cheat sheet (? or K toggles it) */}
            {sketchOn && cheatOpen && (
              <div className="absolute inset-0 z-30 bg-black/50 flex items-center justify-center p-3" onClick={() => setCheatOpen(false)}>
                <div
                  data-testid="kbd-cheatsheet"
                  onClick={e => e.stopPropagation()}
                  className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-full overflow-y-auto p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-gray-900 flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary">keyboard</span> Keyboard shortcuts
                    </h4>
                    <button onClick={() => setCheatOpen(false)} className="text-gray-400 hover:text-gray-700" title="Close (Esc)">
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </div>
                  <ShortcutList compact />
                </div>
              </div>
            )}
    </div>
  );
}
